import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveTopicJob, clearActiveTopicJob } from '../../store/slices/analyticsSlice';
import * as analyticsApi from '../../api/analytics';
import * as triviaApi from '../../api/trivia';
import LakersTriviaChallengeModal from './LakersTriviaChallengeModal';
import AdvancedTopicConfig from './AdvancedTopicConfig';

// ============================================================================
// TOPIC WORKBENCH - The Main Event
// ============================================================================

function TopicsTab() {
  const dispatch = useDispatch();
  const activeTopicJob = useSelector((state) => state.analytics.activeTopicJob);
  const [configs, setConfigs] = useState([]);
  const [views, setViews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const selectedConfigRef = useRef(null);
  useEffect(() => { selectedConfigRef.current = selectedConfig; }, [selectedConfig]);
  const [embeddingsStatus, setEmbeddingsStatus] = useState({});
  const [columnValidation, setColumnValidation] = useState({}); // configId → { valid, missing_columns, available_columns }
  const [editingColumnsConfig, setEditingColumnsConfig] = useState(null); // config being edited
  const [pushToCRMConfig, setPushToCRMConfig] = useState(null); // config for push-to-CRM modal

  // Advanced Mode (Lakers trivia gated)
  const [showTriviaChallenge, setShowTriviaChallenge] = useState(false);
  const [advancedModeUnlocked, setAdvancedModeUnlocked] = useState(false);
  const [showAdvancedConfig, setShowAdvancedConfig] = useState(false);
  const [pendingAdvancedConfig, setPendingAdvancedConfig] = useState(null); // Stores advanced config to apply to next create

  // Check advanced mode unlock status on mount
  useEffect(() => {
    const checkAdvancedStatus = async () => {
      try {
        const res = await triviaApi.getTriviaStatus();
        const data = res.data || res;
        setAdvancedModeUnlocked(data.unlocked || false);
      } catch (err) {
        // Silently fail - not critical
        // Silently fail - not critical
      }
    };
    checkAdvancedStatus();
  }, []);

  // Load topic configs and views
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [configsRes, viewsRes] = await Promise.all([
        analyticsApi.fetchTopicConfigs(),
        analyticsApi.fetchViews(),
      ]);
      const cfgs = configsRes.data?.configs || configsRes.configs || [];
      setConfigs(cfgs);
      setViews(viewsRes.data?.views || viewsRes.views || []);
      // Validate column mappings for all configs in the background
      cfgs.forEach(async (cfg) => {
        try {
          const res = await analyticsApi.validateTopicColumns(cfg.id);
          const data = res.data || res;
          setColumnValidation(prev => ({ ...prev, [cfg.id]: data }));
        } catch (_) { /* non-critical */ }
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load topic configurations');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Track configs that have had auto-clustering triggered
  const [autoClusteredConfigs, setAutoClusteredConfigs] = useState(new Set());

  // Poll embeddings status for configs that are generating
  // Auto-run initial clustering when embeddings complete
  useEffect(() => {
    const generatingConfigs = configs.filter(c => c.status === 'generating');
    if (generatingConfigs.length === 0) return;

    const pollStatus = async () => {
      for (const config of generatingConfigs) {
        try {
          const res = await analyticsApi.fetchTopicEmbeddingsStatus(config.id);
          const data = res.data || res;
          setEmbeddingsStatus(prev => ({ ...prev, [config.id]: data }));

          if (data.status === 'ready') {
            // Auto-run initial clustering if not already done
            if (!autoClusteredConfigs.has(config.id)) {
              setAutoClusteredConfigs(prev => new Set([...prev, config.id]));

              try {
                // Create initial session (or use existing one if already created)
                let session;
                try {
                  const sessionRes = await analyticsApi.createTopicSession(config.id, {
                    name: 'Initial Discovery',
                  });
                  session = sessionRes.data || sessionRes;
                } catch (createErr) {
                  // 400/409 likely means a session already exists — fetch it instead
                  const existing = await analyticsApi.fetchTopicSessions(config.id);
                  const sessions = (existing.data || existing).sessions || existing.data || [];
                  session = Array.isArray(sessions) ? sessions[0] : sessions;
                  if (!session?.id) throw createErr;
                }

                // Run clustering with config's algorithm settings
                const resolution = config.expected_resolution || 1.0;
                const expectedTopics = config.expected_topics || 10;
                const clusteringRequest = {
                  method: config.clustering_algorithm || 'leiden',
                  resolution: resolution,
                  expected_topics: expectedTopics,  // For sample limiting on large datasets
                };
                // Add algorithm-specific params if present
                if (config.clustering_params && Object.keys(config.clustering_params).length > 0) {
                  clusteringRequest.clustering_params = config.clustering_params;
                }
                // Add dimension reduction if configured
                if (config.dim_reduction_method) {
                  clusteringRequest.dim_reduction_method = config.dim_reduction_method;
                  if (config.dim_reduction_params && Object.keys(config.dim_reduction_params).length > 0) {
                    clusteringRequest.dim_reduction_params = config.dim_reduction_params;
                  }
                }
                const clusterRes = await analyticsApi.clusterTopicSession(session.id, clusteringRequest);
                const clusterData = clusterRes.data || clusterRes;
                // Show progress panel for async clustering (no clusters returned yet)
                if (!clusterData?.clusters?.length) {
                  dispatch(setActiveTopicJob({
                    type: 'clustering',
                    status: 'running',
                    configId: config.id,
                    configName: config.name,
                    sessionId: session.id,
                    startedAt: new Date().toISOString(),
                  }));
                }

                if (import.meta.env.DEV) console.log(`Auto-clustered config ${config.id} with ${config.clustering_algorithm || 'leiden'}`);
              } catch (clusterErr) {
                console.error('Auto-clustering failed:', clusterErr);
                // Don't fail - user can manually cluster in workbench
              }
            }

            loadData(); // Refresh to update config status
            // Auto-open workbench if nothing is currently open
            if (!selectedConfigRef.current) {
              setSelectedConfig(config);
            }
          }
        } catch (err) {
          console.error('Failed to fetch embeddings status:', err);
        }
      }
    };

    pollStatus();
    const interval = setInterval(pollStatus, 5000);
    return () => clearInterval(interval);
  }, [configs, loadData, autoClusteredConfigs]);

  const handleCreateConfig = async (formData, options = {}) => {
    const { skipAutoStart = false } = options;
    try {
      const res = await analyticsApi.createTopicConfig(formData);
      const newConfig = res.data || res;
      setShowCreateModal(false);
      setShowTutorial(false);

      // ALWAYS auto-start embeddings unless explicitly skipped
      if (!skipAutoStart && newConfig.id) {
        try {
          await analyticsApi.prepareTopicEmbeddings(newConfig.id);
        } catch (embErr) {
          console.error('Failed to auto-start embeddings:', embErr);
          // Don't fail the whole operation, user can manually trigger later
        }
      }

      loadData();
      return newConfig;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create topic configuration');
      throw err;
    }
  };

  // Check if this is first time (no configs) to show tutorial
  const shouldShowTutorial = configs.length === 0 && !isLoading;

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this topic configuration?')) return;
    try {
      await analyticsApi.deleteTopicConfig(configId);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete topic configuration');
    }
  };

  const handlePrepareEmbeddings = async (configId) => {
    try {
      await analyticsApi.prepareTopicEmbeddings(configId);
      loadData(); // Refresh to show generating status
      const cfg = configs.find(c => c.id === configId);
      dispatch(setActiveTopicJob({
        type: 'embedding',
        status: 'running',
        configId,
        configName: cfg?.name || 'Topic Config',
        startedAt: new Date().toISOString(),
      }));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to start embedding generation');
    }
  };

  const handleSaveColumns = async (configId, newMappings) => {
    try {
      const res = await analyticsApi.updateTopicConfig(configId, { column_mappings: newMappings });
      const updated = res.data || res;
      setEditingColumnsConfig(null);
      // Optimistically update selectedConfig with new columns AND server-reflected status
      setSelectedConfig(prev => prev?.id === configId ? {
        ...prev,
        column_mappings: newMappings,
        ...(updated?.status ? { status: updated.status } : {}),
      } : prev);
      loadData();
      // If server reset status to draft (columns changed embeddings), auto-restart embedding
      if (updated?.status === 'draft') {
        // Clear auto-cluster tracking so it fires again when new embeddings are ready
        setAutoClusteredConfigs(prev => {
          const next = new Set(prev);
          next.delete(configId);
          return next;
        });
        try {
          await analyticsApi.prepareTopicEmbeddings(configId);
          loadData(); // Refresh to show 'generating' status
          const cfg = configs.find(c => c.id === configId);
          dispatch(setActiveTopicJob({
            type: 'embedding',
            status: 'running',
            configId,
            configName: cfg?.name || updated?.name || 'Topic Config',
            startedAt: new Date().toISOString(),
          }));
        } catch (embErr) {
          console.error('Failed to restart embeddings after column change:', embErr);
        }
      }
      try {
        const valRes = await analyticsApi.validateTopicColumns(configId);
        setColumnValidation(prev => ({ ...prev, [configId]: valRes.data || valRes }));
      } catch (_) {}
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update column mappings');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 border border-gray-300',
      generating: 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 border border-blue-300',
      ready: 'bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-700 border border-emerald-300',
      active: 'bg-gradient-to-r from-violet-100 to-purple-100 text-violet-700 border border-violet-300',
      archived: 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-500 border border-gray-300',
    };
    return (
      <span className={`px-3 py-1 text-xs rounded-full font-semibold shadow-sm ${styles[status] || styles.draft}`}>
        {status === 'generating' && (
          <svg className="animate-spin inline h-3 w-3 mr-1.5" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        )}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-violet-200 rounded-full animate-pulse"></div>
            <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-violet-600 rounded-full animate-spin"></div>
          </div>
          <p className="text-gray-600 mt-4 font-medium">Loading topic configurations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="ml-3 text-red-400 hover:text-red-600 transition">
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Hero Header */}
      <div className="bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold">Topic Discovery</h2>
            </div>
            <p className="text-violet-100 max-w-lg">
              Automatically discover and categorize topics from your Salesforce data using AI-powered clustering.
              Turn unstructured text into actionable insights.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Advanced Mode Button */}
            <button
              onClick={() => advancedModeUnlocked ? setShowAdvancedConfig(true) : setShowTriviaChallenge(true)}
              className={`px-4 py-2.5 rounded-xl font-semibold transition shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center gap-2 ${
                advancedModeUnlocked
                  ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white hover:from-amber-500 hover:to-orange-600'
                  : 'bg-white/20 text-white hover:bg-white/30 border border-white/30'
              }`}
              title={advancedModeUnlocked ? 'Advanced Mode Unlocked' : 'Unlock Advanced Mode'}
            >
              {advancedModeUnlocked ? (
                <>
                  <span className="text-lg">🏆</span>
                  Advanced
                </>
              ) : (
                <>
                  <span className="text-lg">🔒</span>
                  Advanced
                </>
              )}
            </button>

            <button
              data-testid="topics-create-button"
              onClick={() => setShowCreateModal(true)}
              className="bg-white text-violet-700 px-5 py-2.5 rounded-xl hover:bg-violet-50 transition font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Topic Config
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Configs Grid */}
      {configs.length === 0 ? (
        <div data-testid="topics-empty-state" className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="bg-gradient-to-br from-violet-100 to-purple-100 rounded-2xl p-6 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
              <svg className="w-12 h-12 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">No Topic Configurations Yet</h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Create your first topic configuration to start discovering patterns and categories in your data.
              Our AI will automatically cluster similar records and suggest meaningful labels.
            </p>
            <button
              data-testid="topics-create-button"
              onClick={() => setShowTutorial(true)}
              className="bg-gradient-to-r from-violet-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:from-violet-700 hover:to-purple-700 transition font-semibold shadow-lg hover:shadow-xl"
            >
              Get Started with Topic Discovery
            </button>
          </div>
        </div>
      ) : (
        <div data-testid="topics-config-list" className="grid gap-4">
          {configs.map((config) => (
            <ConfigCard
              key={config.id}
              config={config}
              embeddingsStatus={embeddingsStatus[config.id]}
              columnValidation={columnValidation[config.id]}
              getStatusBadge={getStatusBadge}
              onPrepareEmbeddings={handlePrepareEmbeddings}
              onOpenWorkbench={() => setSelectedConfig(config)}
              onDelete={handleDeleteConfig}
              onEditColumns={setEditingColumnsConfig}
              onPushToCRM={setPushToCRMConfig}
              activeTopicJob={activeTopicJob}
            />
          ))}
        </div>
      )}

      {/* Edit Columns Modal */}
      {editingColumnsConfig && (
        <EditColumnsModal
          config={editingColumnsConfig}
          columnValidation={columnValidation[editingColumnsConfig.id]}
          onClose={() => setEditingColumnsConfig(null)}
          onSave={(mappings) => handleSaveColumns(editingColumnsConfig.id, mappings)}
        />
      )}

      {/* Push to CRM Modal */}
      {pushToCRMConfig && (
        <PushToCRMModal
          config={pushToCRMConfig}
          onClose={() => setPushToCRMConfig(null)}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateTopicConfigModal
          views={views}
          onClose={() => {
            setShowCreateModal(false);
            setPendingAdvancedConfig(null); // Clear pending config on close
          }}
          onCreate={handleCreateConfig}
          advancedConfig={pendingAdvancedConfig}
        />
      )}

      {/* Tutorial Wizard */}
      {showTutorial && (
        <TopicTutorialWizard
          views={views}
          onClose={() => setShowTutorial(false)}
          onCreate={handleCreateConfig}
          onOpenWorkbench={(config) => {
            setShowTutorial(false);
            setSelectedConfig(config);
          }}
        />
      )}

      {/* Workbench Modal */}
      {selectedConfig && (
        <TopicWorkbench
          config={selectedConfig}
          columnValidation={columnValidation[selectedConfig.id]}
          onEditColumns={() => setEditingColumnsConfig(selectedConfig)}
          onClose={() => setSelectedConfig(null)}
        />
      )}

      {/* Lakers Trivia Challenge Modal */}
      {showTriviaChallenge && (
        <LakersTriviaChallengeModal
          onClose={() => setShowTriviaChallenge(false)}
          onUnlock={() => {
            setAdvancedModeUnlocked(true);
            setShowTriviaChallenge(false);
            // Show advanced config after a brief celebration
            setTimeout(() => setShowAdvancedConfig(true), 500);
          }}
        />
      )}

      {/* Advanced Topic Config Modal */}
      {showAdvancedConfig && (
        <AdvancedConfigModal
          onClose={() => setShowAdvancedConfig(false)}
          onApply={(config) => {
            setPendingAdvancedConfig(config);
            setShowAdvancedConfig(false);
            setShowCreateModal(true);
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// ADVANCED CONFIG MODAL - Wrapper for AdvancedTopicConfig
// ============================================================================

function AdvancedConfigModal({ onClose, onApply }) {
  const [currentConfig, setCurrentConfig] = useState(null);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="advanced-topic-config-modal-title">
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🏆</span>
              <div>
                <h3 id="advanced-topic-config-modal-title" className="text-xl font-bold text-white">Advanced Topic Configuration</h3>
                <p className="text-amber-100 text-sm">Full control over clustering algorithms</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition p-1 hover:bg-white/10 rounded-lg"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          <AdvancedTopicConfig
            showTriviaGate={false}
            onConfigChange={(config) => setCurrentConfig(config)}
          />
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600 mb-4">
              Configure advanced clustering settings above, then use these settings when creating a new topic configuration.
            </p>
            <button
              onClick={() => onApply(currentConfig)}
              className="w-full py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-semibold hover:from-violet-700 hover:to-purple-700 transition"
            >
              Create Topic Config with These Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// EDIT COLUMNS MODAL
// ============================================================================

function EditColumnsModal({ config, columnValidation: initialValidation, onClose, onSave }) {
  // roleFor tracks column→role; absent means Skip
  const [roleFor, setRoleFor] = useState(() => {
    const m = {};
    (config.column_mappings || []).forEach(({ column, role }) => { m[column] = role; });
    return m;
  });
  const [search, setSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [liveValidation, setLiveValidation] = useState(initialValidation);
  const [dataSources, setDataSources] = useState([]);
  const [selectedDataSourceId, setSelectedDataSourceId] = useState(config.data_source_id || '');
  const columnValidation = liveValidation;

  // On mount: if validateTopicColumns returned no columns, fall back to the view's own schema
  useEffect(() => {
    const hasColumns = (initialValidation?.available_columns || []).length > 0;
    if (hasColumns || !config.source_view_id) return;
    analyticsApi.fetchView(config.source_view_id).then(res => {
      const fullView = res.data || res;
      const cols = (fullView?.output_schema?.columns || [])
        .filter(c => !NOISE_COLUMN_PATTERNS.test(c.name) && !NUMERIC_TYPES.has(c.type))
        .map(c => c.name);
      if (cols.length > 0) {
        setLiveValidation(prev => ({ ...(prev || {}), available_columns: cols }));
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load data sources so the sync button knows which one to target
  useEffect(() => {
    if (config.data_source_id) return; // already known
    import('../../api/auth').then(({ listDataSources }) => {
      listDataSources().then(res => {
        const list = res.data_sources || res.dataSources || res || [];
        setDataSources(list);
        if (list.length === 1) setSelectedDataSourceId(list[0].data_source_id || list[0].id);
      }).catch(() => {});
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSyncData = async () => {
    const dsId = config.data_source_id || selectedDataSourceId;
    if (!dsId) {
      setSyncMessage('Please select a data source first.');
      return;
    }
    setIsSyncing(true);
    setSyncMessage('Starting sync…');
    try {
      await analyticsApi.triggerFieldSync([], dsId);
      setSyncMessage('Sync running — checking for columns…');
      // Poll validateTopicColumns until columns appear, up to ~2 min
      for (let i = 0; i < 24; i++) {
        await new Promise(r => setTimeout(r, 5000));
        try {
          const res = await analyticsApi.validateTopicColumns(config.id);
          const data = res.data || res;
          if ((data.available_columns || []).length > 0) {
            setLiveValidation(data);
            setSyncMessage('');
            setIsSyncing(false);
            return;
          }
        } catch (_) {}
        setSyncMessage(`Sync running — checking for columns… (${(i + 1) * 5}s)`);
      }
      setSyncMessage('Sync is still running. Close and reopen this dialog in a minute.');
    } catch (err) {
      setSyncMessage('Sync failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSyncing(false);
    }
  };

  const availableCols = useMemo(() => {
    const cols = columnValidation?.available_columns || [];
    // Also include any columns already in config that may not be in available list
    const extra = (config.column_mappings || []).map(m => m.column).filter(c => !cols.includes(c));
    return [...cols, ...extra].sort();
  }, [columnValidation, config.column_mappings]);

  const missingSet = new Set(columnValidation?.missing_columns || []);

  const filtered = useMemo(() => {
    if (!search.trim()) return availableCols;
    const q = search.toLowerCase();
    return availableCols.filter(c => c.toLowerCase().includes(q));
  }, [availableCols, search]);

  const selectedCount = Object.keys(roleFor).length;

  const ROLE_COLORS = {
    primary:     'bg-violet-100 border-violet-400 text-violet-800',
    context:     'bg-blue-100 border-blue-400 text-blue-800',
    categorical: 'bg-emerald-100 border-emerald-400 text-emerald-800',
  };

  const cycleRole = (col) => {
    setRoleFor(prev => {
      const current = prev[col];
      if (!current) return { ...prev, [col]: 'primary' };
      if (current === 'primary') return { ...prev, [col]: 'context' };
      if (current === 'context') return { ...prev, [col]: 'categorical' };
      const next = { ...prev };
      delete next[col];
      return next;
    });
  };

  const setRole = (col, role) => {
    setRoleFor(prev => {
      if (role === 'skip') {
        const next = { ...prev };
        delete next[col];
        return next;
      }
      return { ...prev, [col]: role };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const mappings = Object.entries(roleFor).map(([column, role]) => ({ column, role }));
    setIsSaving(true);
    try {
      await onSave(mappings);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-4 flex justify-between items-center flex-shrink-0">
          <div>
            <h3 className="text-xl font-bold text-white">Configure Columns</h3>
            <p className="text-violet-100 text-sm mt-0.5">{config.name} · choose which fields the AI reads</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Role legend */}
          <div className="px-6 pt-4 pb-2 flex-shrink-0">
            <div className="flex flex-wrap gap-3 text-xs">
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-100 border border-violet-300 font-semibold text-violet-800">
                <span className="w-2 h-2 rounded-full bg-violet-500 inline-block" /> Primary — main text to embed
              </span>
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-100 border border-blue-300 font-semibold text-blue-800">
                <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" /> Context — supporting detail
              </span>
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 border border-emerald-300 font-semibold text-emerald-800">
                <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> Categorical — discrete values
              </span>
            </div>
          </div>

          {/* Warning */}
          {selectedCount > 0 && (config.column_mappings || []).length > 0 && (
            <div className="mx-6 mb-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-xs text-amber-800 flex-shrink-0">
              Changing columns invalidates existing embeddings — you'll need to re-run preparation and clustering.
            </div>
          )}

          {/* Search */}
          <div className="px-6 pb-3 flex-shrink-0">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                placeholder={`Search ${availableCols.length} columns…`}
                autoFocus
              />
              {search && (
                <button type="button" onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1.5">
              {selectedCount} column{selectedCount !== 1 ? 's' : ''} selected
              {search && ` · showing ${filtered.length} of ${availableCols.length}`}
            </p>
          </div>

          {/* Column list */}
          <div className="flex-1 overflow-y-auto px-6 pb-4 min-h-0">
            {availableCols.length === 0 ? (
              <div className="text-center py-10 px-4">
                <div className="text-5xl mb-4">📭</div>
                <p className="font-bold text-gray-800 text-lg mb-2">No synced columns yet</p>
                <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                  We need to pull your data before we can list columns. Hit Sync Data to kick it off — usually takes under a minute.
                </p>
                {/* Data source picker when config has no data_source_id */}
                {!config.data_source_id && dataSources.length > 1 && !isSyncing && !syncMessage && (
                  <div className="mb-4 w-full max-w-xs mx-auto">
                    <label className="block text-xs font-semibold text-gray-600 mb-1 text-left">Data source to sync from</label>
                    <select
                      value={selectedDataSourceId}
                      onChange={e => setSelectedDataSourceId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="">Select…</option>
                      {dataSources.map(ds => (
                        <option key={ds.data_source_id || ds.id} value={ds.data_source_id || ds.id}>{ds.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {syncMessage ? (
                  <div className="flex items-center justify-center gap-2 text-violet-700 text-sm font-medium">
                    {isSyncing && (
                      <svg className="animate-spin w-4 h-4 flex-shrink-0" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    )}
                    {syncMessage}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleSyncData}
                    disabled={isSyncing || (!config.data_source_id && !selectedDataSourceId)}
                    className="bg-gradient-to-r from-violet-600 to-purple-600 text-white px-8 py-3 rounded-xl font-semibold text-base shadow-lg hover:from-violet-700 hover:to-purple-700 transition disabled:opacity-50 flex items-center gap-2 mx-auto"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Sync Data
                  </button>
                )}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">No columns match "{search}"</div>
            ) : (
              <div className="space-y-1.5">
                {filtered.map(col => {
                  const role = roleFor[col];
                  const isMissing = missingSet.has(col);
                  const rowClass = role
                    ? `border-2 ${ROLE_COLORS[role]}`
                    : 'bg-gray-50 border border-gray-200 text-gray-700';
                  return (
                    <div key={col} className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${rowClass}`}>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-sm">{col}</span>
                        {isMissing && (
                          <span className="ml-2 text-xs text-red-600 font-semibold">⚠ not synced</span>
                        )}
                      </div>
                      <select
                        value={role || 'skip'}
                        onChange={e => setRole(col, e.target.value)}
                        className={`text-sm border rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-violet-500 font-medium flex-shrink-0 ${
                          role ? 'border-current' : 'border-gray-300 text-gray-500'
                        }`}
                      >
                        <option value="skip">Skip</option>
                        <option value="primary">Primary</option>
                        <option value="context">Context</option>
                        <option value="categorical">Categorical</option>
                      </select>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0 bg-gray-50">
            <span className="text-sm text-gray-600">
              {selectedCount === 0
                ? <span className="text-red-600 font-medium">Select at least one column</span>
                : <span><strong className="text-violet-700">{selectedCount}</strong> column{selectedCount !== 1 ? 's' : ''} will be embedded</span>
              }
            </span>
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition text-sm font-medium">
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving || selectedCount === 0}
                className="px-6 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg hover:from-violet-700 hover:to-purple-700 transition font-semibold disabled:opacity-50 text-sm"
              >
                {isSaving ? 'Saving…' : 'Save Columns'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// PUSH TO CRM MODAL - Write topic labels back to a Salesforce field
// ============================================================================

function PushToCRMModal({ config, onClose }) {
  const [dataSources, setDataSources] = useState([]);
  const [dataSourceId, setDataSourceId] = useState('');
  const [targetField, setTargetField] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Fetch Salesforce data sources on mount
  useEffect(() => {
    import('../../api/auth').then(({ listDataSources }) => {
      listDataSources().then(res => {
        const sfSources = (res.data_sources || res.dataSources || res || [])
          .filter(ds => ds.source_type === 'salesforce' || ds.sourceType === 'salesforce');
        setDataSources(sfSources);
        if (sfSources.length === 1) setDataSourceId(sfSources[0].data_source_id || sfSources[0].id);
      }).catch(() => {});
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!targetField || !dataSourceId) return;
    setIsSubmitting(true);
    setError(null);
    try {
      // Find the active model ID from config — assume config.active_model_id is provided
      const modelId = config.active_model_id;
      if (!modelId) throw new Error('No active model found. Run classification first.');
      const res = await analyticsApi.pushTopicsToCRM(modelId, {
        target_field: targetField,
        data_source_id: dataSourceId,
      });
      setResult(res);
    } catch (err) {
      setError(err.message || 'Failed to start write-back');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">Push Labels to Salesforce</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Write <code className="bg-gray-100 px-1 rounded">bnstopic_{config.topic_column_name}_label</code> values
          to a custom Salesforce field on <strong>{config.target_sobject}</strong> records.
        </p>
        {result ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
            <div className="font-semibold mb-1">Write-back started</div>
            <div>{result.records_staged?.toLocaleString()} records staged</div>
            <div className="text-xs mt-1 text-green-600">Workflow: {result.workflow_id}</div>
            <div className="text-xs mt-1">Track progress in the DataOps tab.</div>
            <button onClick={onClose} className="mt-3 px-4 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700">
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Salesforce Data Source</label>
              <select
                value={dataSourceId}
                onChange={e => setDataSourceId(e.target.value)}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select data source…</option>
                {dataSources.map(ds => (
                  <option key={ds.data_source_id || ds.id} value={ds.data_source_id || ds.id}>
                    {ds.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Salesforce Field API Name</label>
              <input
                type="text"
                value={targetField}
                onChange={e => setTargetField(e.target.value)}
                placeholder="e.g. Topic_Label__c"
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-400 mt-1">Must be an existing custom field on {config.target_sobject}</p>
            </div>
            {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg p-2">{error}</p>}
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button
                type="submit"
                disabled={isSubmitting || !targetField || !dataSourceId}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
              >
                {isSubmitting ? 'Starting…' : 'Push to Salesforce'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// CONFIG CARD - Beautiful Card for Each Topic Config
// ============================================================================

function ConfigCard({ config, embeddingsStatus, columnValidation, getStatusBadge, onPrepareEmbeddings, onOpenWorkbench, onDelete, onEditColumns, onPushToCRM, activeTopicJob }) {
  const progress = embeddingsStatus?.progress || 0;
  const hasMissingColumns = columnValidation && !columnValidation.valid && columnValidation.missing_columns?.length > 0;

  // Is an async job running for this specific config?
  const jobForThis = activeTopicJob?.configId === config.id && activeTopicJob?.status === 'running'
    ? activeTopicJob : null;
  const isClustering = jobForThis?.type === 'clustering';
  const isEmbedding = jobForThis?.type === 'embedding';

  // Determine what action is available based on status
  const isReady = !isClustering && (config.status === 'ready' || config.status === 'active');

  return (
    <div data-testid={`topics-config-card-${config.id}`} className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-300">
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 data-testid={`topics-config-name-${config.id}`} className="text-lg font-bold text-gray-900">{config.name}</h3>
              <span data-testid={`topics-config-status-${config.id}`}>{getStatusBadge(config.status)}</span>
              {/* Missing columns warning badge */}
              {hasMissingColumns && (
                <span
                  data-testid={`topics-column-warning-${config.id}`}
                  className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 border border-red-200 rounded-full flex items-center gap-1 cursor-pointer"
                  onClick={onOpenWorkbench}
                  title={`Missing columns: ${columnValidation.missing_columns.join(', ')}`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Column mismatch
                </span>
              )}
              {/* Advanced algorithm badge */}
              {config.clustering_algorithm && config.clustering_algorithm !== 'leiden' && (
                <span className="px-2 py-0.5 text-xs font-medium bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 border border-amber-200 rounded-full flex items-center gap-1">
                  <span>🏆</span>
                  {config.clustering_algorithm.toUpperCase()}
                </span>
              )}
            </div>
            {config.description && (
              <p className="text-gray-600 text-sm mb-3">{config.description}</p>
            )}
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center gap-1.5 text-gray-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
                <span>{config.source_view_name || 'Unknown view'}</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <span>{config.target_sobject}</span>
              </div>
              <div className="flex items-center gap-1.5 text-violet-600 font-medium">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <span>bnstopic_{config.topic_column_name}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4">
            {/* Clustering in progress */}
            {isClustering && (
              <button disabled className="px-4 py-2 bg-violet-100 text-violet-600 text-sm font-semibold rounded-lg flex items-center gap-1.5 cursor-not-allowed">
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Clustering…
              </button>
            )}
            {/* Embedding in progress */}
            {isEmbedding && (
              <button disabled className="px-4 py-2 bg-blue-50 text-blue-600 text-sm font-semibold rounded-lg flex items-center gap-1.5 cursor-not-allowed">
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Generating…
              </button>
            )}
            {/* Draft status — show Prepare button to start embedding generation */}
            {config.status === 'draft' && !isEmbedding && !isClustering && (
              <button
                data-testid={`topics-discover-${config.id}`}
                onClick={() => onPrepareEmbeddings(config.id)}
                className="px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white text-sm font-semibold rounded-lg hover:from-violet-600 hover:to-purple-600 transition shadow-md hover:shadow-lg flex items-center gap-1.5"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Prepare
              </button>
            )}
            {/* Ready/active — show Open Workbench */}
            {isReady && (
              <button
                data-testid={`topics-classify-${config.id}`}
                onClick={onOpenWorkbench}
                className="px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white text-sm font-semibold rounded-lg hover:from-violet-600 hover:to-purple-600 transition shadow-md hover:shadow-lg"
              >
                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Open Workbench
                </span>
              </button>
            )}
            {/* Push to Salesforce — only when active model has been applied */}
            {config.status === 'active' && (
              <button
                data-testid={`topics-push-to-crm-${config.id}`}
                onClick={() => onPushToCRM(config)}
                className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition"
                title="Push topic labels to Salesforce"
              >
                Push to SF
              </button>
            )}
            <button
              data-testid={`topics-edit-columns-${config.id}`}
              onClick={() => onEditColumns(config)}
              className="p-2 text-gray-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition"
              title="Edit column mappings"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            </button>
            <button
              data-testid={`topics-delete-${config.id}`}
              onClick={() => onDelete(config.id)}
              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
              title="Delete configuration"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Progress bar for generating status */}
        {config.status === 'generating' && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-600 flex items-center gap-2">
                <svg className="animate-spin w-4 h-4 text-violet-500" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Analyzing your data...
              </span>
              <span className="font-semibold text-violet-600">{Math.round(progress * 100)}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all duration-500"
                style={{ width: `${progress * 100}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {embeddingsStatus?.records_processed
                ? `${embeddingsStatus.records_processed.toLocaleString()} / ${embeddingsStatus.total_records?.toLocaleString() || '?'} records • `
                : ''}
              Topics will be discovered automatically when complete
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// TOPIC TUTORIAL WIZARD - Step-by-step guided onboarding
// ============================================================================

function TopicTutorialWizard({ views, onClose, onCreate, onOpenWorkbench }) {
  const [step, setStep] = useState(1);
  const [selectedView, setSelectedView] = useState(null);
  const [viewSchema, setViewSchema] = useState(null);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [columnMappings, setColumnMappings] = useState([]); // [{column, role}]
  const [expectedTopics, setExpectedTopics] = useState('medium'); // 'few', 'medium', 'many', 'custom'
  const [customTopicCount, setCustomTopicCount] = useState(15);
  const [topicName, setTopicName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [error, setError] = useState(null);
  const [createdConfig, setCreatedConfig] = useState(null);

  // Derive target sObject from view (simplified - assumes view name or first table)
  const derivedSObject = useMemo(() => {
    if (!selectedView) return '';
    // Try to extract from view name or use a default
    const name = selectedView.name?.toLowerCase() || '';
    if (name.includes('case')) return 'Case';
    if (name.includes('account')) return 'Account';
    if (name.includes('contact')) return 'Contact';
    if (name.includes('opportunity')) return 'Opportunity';
    if (name.includes('lead')) return 'Lead';
    return 'Case'; // Default
  }, [selectedView]);

  // Fetch view schema when user picks a view
  const handleSelectView = useCallback(async (view) => {
    setSelectedView(view);
    setViewSchema(null);
    setColumnMappings([]);
    setLoadingSchema(true);
    try {
      const res = await analyticsApi.fetchView(view.id);
      const fullView = res.data || res;
      setViewSchema(fullView);
      const cols = fullView?.output_schema?.columns || [];
      const meaningful = cols.filter(c => !NOISE_COLUMN_PATTERNS.test(c.name) && !NUMERIC_TYPES.has(c.type));
      setColumnMappings(meaningful.map(c => ({ column: c.name, role: suggestRole(c) })).filter(m => m.role !== null));
    } catch (_) {
      // non-fatal — user can still proceed
    } finally {
      setLoadingSchema(false);
    }
  }, []);

  const meaningfulColumns = (viewSchema?.output_schema?.columns || []).filter(
    c => !NOISE_COLUMN_PATTERNS.test(c.name) && !NUMERIC_TYPES.has(c.type)
  );

  const updateColumnRole = (colName, role) => {
    setColumnMappings(prev => {
      if (role === 'none') return prev.filter(m => m.column !== colName);
      const existing = prev.find(m => m.column === colName);
      if (existing) return prev.map(m => m.column === colName ? { ...m, role } : m);
      return [...prev, { column: colName, role }];
    });
  };

  const getRoleForColumn = (colName) => columnMappings.find(m => m.column === colName)?.role || 'none';

  // Map expected topics to resolution
  const getResolution = () => {
    switch (expectedTopics) {
      case 'few': return 0.5; // ~5-10 topics
      case 'medium': return 1.0; // ~10-20 topics
      case 'many': return 1.5; // ~20-40 topics
      case 'custom': return Math.max(0.3, Math.min(2.5, customTopicCount / 15));
      default: return 1.0;
    }
  };

  const handleNext = () => {
    if (step < 5) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleFinish = async () => {
    if (!selectedView || !topicName.trim()) {
      setError('Please complete all required fields');
      return;
    }

    setIsProcessing(true);
    setProcessingStatus('Creating topic configuration...');
    setError(null);

    try {
      // Calculate expected topics from selection
      const getExpectedTopicsCount = () => {
        switch (expectedTopics) {
          case 'few': return 8;
          case 'medium': return 15;
          case 'many': return 30;
          case 'custom': return customTopicCount;
          default: return 15;
        }
      };

      // Create the config with expected_resolution and expected_topics for auto-clustering
      const formData = {
        name: topicName.trim(),
        description: `Topic configuration for ${selectedView.name}`,
        source_view_id: selectedView.id,
        target_sobject: derivedSObject,
        topic_column_name: topicName.trim().toLowerCase().replace(/[^a-z0-9]/g, '_'),
        column_mappings: columnMappings,
        expected_resolution: getResolution(),
        expected_topics: getExpectedTopicsCount(),  // For sample limiting on large datasets
      };

      // Create config - auto-start is now default in handleCreateConfig
      await onCreate(formData);

      // Show brief success message, then close
      setProcessingStatus('Started! Your topics will be ready shortly.');
      setTimeout(() => {
        onClose(); // Close wizard - main component shows progress
      }, 1500);

    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create topic configuration');
      setIsProcessing(false);
    }
  };

  const steps = [
    { num: 1, title: 'Welcome', icon: '✨' },
    { num: 2, title: 'Select Data', icon: '📊' },
    { num: 3, title: 'Columns', icon: '🗂️' },
    { num: 4, title: 'Configure', icon: '⚙️' },
    { num: 5, title: 'Launch', icon: '🚀' },
  ];

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="topic-discovery-setup-modal-title">
        {/* Progress Header */}
        <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 px-6 py-5">
          <div className="flex items-center justify-between mb-4">
            <h2 id="topic-discovery-setup-modal-title" className="text-xl font-bold text-white">Topic Discovery Setup</h2>
            <button
              onClick={onClose}
              disabled={isProcessing}
              className="text-white/70 hover:text-white transition p-1 disabled:opacity-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Step Indicators */}
          <div className="flex items-center justify-between">
            {steps.map((s, i) => (
              <div key={s.num} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm transition-all ${
                  step === s.num
                    ? 'bg-white text-violet-600 scale-110 shadow-lg'
                    : step > s.num
                    ? 'bg-white/30 text-white'
                    : 'bg-white/10 text-white/50'
                }`}>
                  {step > s.num ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <span>{s.icon}</span>
                  )}
                </div>
                {i < steps.length - 1 && (
                  <div className={`w-12 h-1 mx-2 rounded-full transition-all ${
                    step > s.num ? 'bg-white/50' : 'bg-white/20'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 min-h-[400px] flex flex-col">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          )}

          {/* Step 1: Welcome */}
          {step === 1 && (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <div className="w-24 h-24 bg-gradient-to-br from-violet-100 to-purple-100 rounded-2xl flex items-center justify-center mb-6">
                <svg className="w-12 h-12 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Welcome to Topic Discovery</h3>
              <p className="text-gray-600 max-w-md mb-6 leading-relaxed">
                Topic Discovery uses AI to automatically find patterns in your text data and organize
                them into meaningful categories. Perfect for analyzing support cases, feedback, or any
                unstructured text.
              </p>
              <div className="grid grid-cols-3 gap-4 w-full max-w-lg">
                <div className="bg-violet-50 rounded-xl p-4 text-center">
                  <div className="text-2xl mb-1">🔍</div>
                  <div className="text-sm font-medium text-violet-700">Discover</div>
                  <div className="text-xs text-violet-500">Find hidden patterns</div>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <div className="text-2xl mb-1">🏷️</div>
                  <div className="text-sm font-medium text-purple-700">Categorize</div>
                  <div className="text-xs text-purple-500">AI-suggested labels</div>
                </div>
                <div className="bg-indigo-50 rounded-xl p-4 text-center">
                  <div className="text-2xl mb-1">📈</div>
                  <div className="text-sm font-medium text-indigo-700">Analyze</div>
                  <div className="text-xs text-indigo-500">Track trends over time</div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Select Data Source */}
          {step === 2 && (
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Select Your Data Source</h3>
              <p className="text-gray-600 mb-6">
                Choose a view that contains the text data you want to analyze. The view should include
                fields like Subject, Description, or Comments.
              </p>

              {views.length === 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
                  <div className="text-3xl mb-3">📋</div>
                  <h4 className="font-semibold text-amber-800 mb-2">No Views Available</h4>
                  <p className="text-amber-700 text-sm">
                    You need to create a view first. Go to the Views tab to create one, then come back here.
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                  {views.map((view) => (
                    <button
                      key={view.id}
                      onClick={() => handleSelectView(view)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        selectedView?.id === view.id
                          ? 'border-violet-500 bg-violet-50 shadow-md'
                          : 'border-gray-200 hover:border-violet-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          selectedView?.id === view.id ? 'bg-violet-500 text-white' : 'bg-gray-100 text-gray-500'
                        }`}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{view.name}</div>
                          {view.description && (
                            <div className="text-sm text-gray-500 line-clamp-1">{view.description}</div>
                          )}
                        </div>
                        {selectedView?.id === view.id && (
                          <svg className="w-6 h-6 text-violet-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Select Columns */}
          {step === 3 && (
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-1">Select Columns to Analyze</h3>
              <p className="text-gray-600 mb-4 text-sm">
                Choose which text fields carry the meaning you want to cluster. The AI will embed these into vectors.
              </p>

              {loadingSchema && (
                <div className="flex items-center gap-3 p-4 bg-violet-50 rounded-xl text-violet-700">
                  <svg className="animate-spin w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span className="text-sm font-medium">Loading columns from {selectedView?.name}…</span>
                </div>
              )}

              {!loadingSchema && meaningfulColumns.length === 0 && viewSchema && (
                <div className="p-5 bg-amber-50 border border-amber-300 rounded-xl text-center">
                  <div className="text-3xl mb-2">⚠️</div>
                  <p className="font-semibold text-amber-800 mb-1">No text columns found</p>
                  <p className="text-amber-700 text-sm">This view has no text fields suitable for clustering. Go back and pick a different view.</p>
                </div>
              )}

              {!loadingSchema && columnMappings.length === 0 && meaningfulColumns.length > 0 && (
                <div className="mb-4 p-4 bg-red-50 border-2 border-red-400 rounded-xl flex items-start gap-3">
                  <svg className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <p className="font-bold text-red-700">You must select at least one column</p>
                    <p className="text-red-600 text-sm mt-0.5">Set a role for at least one column below to continue. Skip means the column won't be used.</p>
                  </div>
                </div>
              )}

              {!loadingSchema && meaningfulColumns.length > 0 && (
                <>
                  <div className="bg-violet-50 rounded-xl p-3 text-xs text-violet-700 mb-3 space-y-0.5">
                    <span className="font-semibold text-violet-800">Primary</span> — core text to embed &nbsp;·&nbsp;
                    <span className="font-semibold text-blue-700">Context</span> — supporting detail &nbsp;·&nbsp;
                    <span className="font-semibold text-emerald-700">Categorical</span> — discrete values (Industry, Stage) &nbsp;·&nbsp;
                    <span className="text-violet-500">Skip — not included</span>
                  </div>
                  <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
                    {meaningfulColumns.map(col => {
                      const role = getRoleForColumn(col.name);
                      const roleColors = {
                        primary: 'bg-violet-100 border-violet-300',
                        context: 'bg-blue-100 border-blue-300',
                        categorical: 'bg-emerald-100 border-emerald-300',
                        none: 'bg-gray-50 border-gray-200',
                      };
                      return (
                        <div key={col.name} className={`flex items-center gap-3 p-3 rounded-lg border transition ${roleColors[role]}`}>
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-sm text-gray-900">{col.name}</span>
                            <span className="text-xs ml-2 text-gray-400">{col.type}</span>
                          </div>
                          <select
                            value={role}
                            onChange={(e) => updateColumnRole(col.name, e.target.value)}
                            className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-violet-500 flex-shrink-0"
                          >
                            <option value="none">Skip</option>
                            <option value="primary">Primary</option>
                            <option value="context">Context</option>
                            <option value="categorical">Categorical</option>
                          </select>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {columnMappings.length} of {meaningfulColumns.length} columns selected
                  </p>
                </>
              )}
            </div>
          )}

          {/* Step 4: Configure */}
          {step === 4 && (
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Configure Your Topics</h3>
              <p className="text-gray-600 mb-6">
                Give your topic configuration a name and tell us roughly how many topics you expect to find.
              </p>

              <div className="space-y-6">
                {/* Topic Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Topic Name</label>
                  <input
                    type="text"
                    value={topicName}
                    onChange={(e) => setTopicName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition text-lg"
                    placeholder="e.g., Support Case Topics"
                  />
                  <p className="text-xs text-gray-500 mt-1.5">
                    This will create columns: bnstopic_{(topicName || 'name').toLowerCase().replace(/[^a-z0-9]/g, '_')}_id, _label, _confidence
                  </p>
                </div>

                {/* Expected Topics */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    How many topics do you expect?
                  </label>
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { value: 'few', label: 'A few', desc: '5-10', icon: '📁' },
                      { value: 'medium', label: 'Medium', desc: '10-20', icon: '📂' },
                      { value: 'many', label: 'Many', desc: '20-40', icon: '🗂️' },
                      { value: 'custom', label: 'Custom', desc: 'Set number', icon: '⚙️' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setExpectedTopics(opt.value)}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          expectedTopics === opt.value
                            ? 'border-violet-500 bg-violet-50'
                            : 'border-gray-200 hover:border-violet-300'
                        }`}
                      >
                        <div className="text-xl mb-1">{opt.icon}</div>
                        <div className="font-medium text-sm text-gray-900">{opt.label}</div>
                        <div className="text-xs text-gray-500">{opt.desc}</div>
                      </button>
                    ))}
                  </div>

                  {expectedTopics === 'custom' && (
                    <div className="mt-3 flex items-center gap-4">
                      <input
                        type="range"
                        min="3"
                        max="100"
                        value={customTopicCount}
                        onChange={(e) => setCustomTopicCount(parseInt(e.target.value))}
                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-violet-600"
                      />
                      <span className="w-16 text-center font-bold text-violet-600">{customTopicCount}</span>
                    </div>
                  )}

                  <p className="text-xs text-gray-500 mt-2">
                    Don't worry - you can always split or merge topics later!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Launch / Processing */}
          {step === 5 && (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              {isProcessing ? (
                <>
                  <div className="relative w-24 h-24 mb-6">
                    <div className="absolute inset-0 border-4 border-violet-200 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-transparent border-t-violet-600 rounded-full animate-spin"></div>
                    <div className="absolute inset-2 bg-gradient-to-br from-violet-100 to-purple-100 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Setting Up Your Topics</h3>
                  <p className="text-gray-600 mb-4">{processingStatus}</p>
                  <div className="w-full max-w-xs bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full animate-pulse" style={{ width: '60%' }} />
                  </div>
                </>
              ) : (
                <>
                  <div className="w-24 h-24 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl flex items-center justify-center mb-6">
                    <svg className="w-12 h-12 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Ready to Launch!</h3>
                  <p className="text-gray-600 max-w-md mb-6">
                    We'll analyze <strong className="text-violet-600">{selectedView?.name}</strong> and
                    discover topics automatically. This process typically takes 1-3 minutes.
                  </p>

                  <div className="bg-gray-50 rounded-xl p-4 w-full max-w-sm text-left space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <svg className="w-4 h-4 text-violet-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700"><strong>Name:</strong> {topicName}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <svg className="w-4 h-4 text-violet-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700"><strong>Data:</strong> {selectedView?.name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <svg className="w-4 h-4 text-violet-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700"><strong>Columns:</strong> {columnMappings.length} selected</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <svg className="w-4 h-4 text-violet-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700"><strong>Expected:</strong> {
                        expectedTopics === 'few' ? '5-10' :
                        expectedTopics === 'medium' ? '10-20' :
                        expectedTopics === 'many' ? '20-40' :
                        customTopicCount
                      } topics</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
          {step > 1 && !isProcessing ? (
            <button
              onClick={handleBack}
              className="px-5 py-2.5 text-gray-700 hover:bg-gray-200 rounded-lg transition font-medium"
            >
              Back
            </button>
          ) : (
            <div />
          )}

          {step < 5 ? (
            <button
              onClick={handleNext}
              disabled={
                (step === 2 && !selectedView) ||
                (step === 3 && (loadingSchema || (meaningfulColumns.length > 0 && columnMappings.length === 0)))
              }
              className="px-6 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:from-violet-700 hover:to-purple-700 transition font-semibold shadow-md disabled:opacity-50"
            >
              Continue
            </button>
          ) : !isProcessing ? (
            <button
              onClick={handleFinish}
              disabled={!topicName.trim() || !selectedView}
              className="px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 transition font-semibold shadow-md disabled:opacity-50 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Launch Topic Discovery
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CREATE TOPIC CONFIG MODAL
// ============================================================================

// Columns that carry no semantic meaning for clustering — skip them by default
const NOISE_COLUMN_PATTERNS = /^(id|.*_id|.*id$|created_date|updated_date|.*date$|.*_at$|owner_id|account_id)/i;
const NUMERIC_TYPES = new Set(['DECIMAL', 'INTEGER', 'FLOAT', 'DOUBLE', 'BIGINT', 'BOOLEAN', 'DATE', 'TIMESTAMP']);

function suggestRole(col) {
  if (NUMERIC_TYPES.has(col.type)) return null; // not useful for embedding
  // Short discrete-value columns tend to be categorical
  if (['Industry', 'Type', 'StageName', 'Status', 'TaskSubtype', 'BillingState', 'BillingCountry', 'LeadSource', 'Rating'].includes(col.name)) return 'categorical';
  return 'primary';
}

function CreateTopicConfigModal({ views, onClose, onCreate, advancedConfig }) {
  const [step, setStep] = useState(1); // 1 = basics, 2 = columns
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    source_view_id: '',
    data_source_id: '',
    target_sobject: '',
    topic_column_name: '',
    column_mappings: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Data sources for incremental classification linking
  const [dataSources, setDataSources] = useState([]);
  useEffect(() => {
    import('../../api/auth').then(({ listDataSources }) => {
      listDataSources().then(res => {
        setDataSources(res.data_sources || res.dataSources || res || []);
      }).catch(() => {});
    });
  }, []);

  // Columns available from the selected view's output_schema (fetched on selection)
  const [viewSchema, setViewSchema] = useState(null);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const selectedView = views.find(v => v.id === formData.source_view_id);
  const availableColumns = viewSchema?.output_schema?.columns || [];
  // Pre-filter noise — only show columns that could carry meaning
  const meaningfulColumns = availableColumns.filter(c =>
    !NOISE_COLUMN_PATTERNS.test(c.name) && !NUMERIC_TYPES.has(c.type)
  );

  // When a view is selected, fetch full view detail to get output_schema
  const handleViewChange = async (viewId) => {
    const view = views.find(v => v.id === viewId);
    const guessedObject = view?.name?.replace(/\s+(base|overview|summary|detail)$/i, '').trim() || '';
    const guessedColumn = guessedObject ? guessedObject.toLowerCase().replace(/[^a-z0-9]/g, '_') + '_topic' : '';
    setFormData(prev => ({ ...prev, source_view_id: viewId, target_sobject: guessedObject, topic_column_name: guessedColumn, column_mappings: [] }));
    setViewSchema(null);
    if (!viewId) return;
    setLoadingSchema(true);
    try {
      const res = await analyticsApi.fetchView(viewId);
      const fullView = res.data || res;
      setViewSchema(fullView);
      const cols = fullView?.output_schema?.columns || [];
      const meaningful = cols.filter(c => !NOISE_COLUMN_PATTERNS.test(c.name) && !NUMERIC_TYPES.has(c.type));
      const suggested = meaningful
        .map(c => ({ column: c.name, role: suggestRole(c) }))
        .filter(m => m.role !== null);
      setFormData(prev => ({ ...prev, column_mappings: suggested }));
    } catch (_) {
      // non-fatal — user can still proceed
    } finally {
      setLoadingSchema(false);
    }
  };

  const updateColumnRole = (colName, role) => {
    setFormData(prev => {
      const existing = prev.column_mappings.find(m => m.column === colName);
      if (role === 'none') {
        return { ...prev, column_mappings: prev.column_mappings.filter(m => m.column !== colName) };
      }
      if (existing) {
        return { ...prev, column_mappings: prev.column_mappings.map(m => m.column === colName ? { ...m, role } : m) };
      }
      return { ...prev, column_mappings: [...prev.column_mappings, { column: colName, role }] };
    });
  };

  const getRoleForColumn = (colName) => {
    return formData.column_mappings.find(m => m.column === colName)?.role || 'none';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step === 1) {
      if (canProceedStep1) setStep(2);
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const requestData = { ...formData };
      if (advancedConfig) {
        requestData.clustering_algorithm = advancedConfig.algorithm;
        requestData.clustering_params = advancedConfig.algorithm_params || {};
        requestData.dim_reduction_method = advancedConfig.dim_reduction;
        requestData.dim_reduction_params = advancedConfig.dim_reduction_params || {};
      }
      await onCreate(requestData);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create configuration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const advanceToStep2 = (e) => {
    if (e) e.preventDefault(); // suppress the subsequent click event
    setStep(2);
  };

  const canProceedStep1 = formData.name && formData.source_view_id && formData.data_source_id;
  const canSubmit = formData.column_mappings.length > 0 && formData.target_sobject && formData.topic_column_name;

  const ROLE_COLORS = {
    primary: 'bg-violet-100 text-violet-700 border-violet-300',
    context: 'bg-blue-100 text-blue-700 border-blue-300',
    categorical: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    none: 'bg-gray-100 text-gray-500 border-gray-200',
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-in fade-in zoom-in duration-200" role="dialog" aria-modal="true" aria-labelledby="create-topic-config-modal-title">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 id="create-topic-config-modal-title" className="text-xl font-bold text-white">Create Topic Configuration</h3>
              <p className="text-violet-100 text-sm mt-1">
                Step {step} of 2 — {step === 1 ? 'Choose a view' : 'Map columns for clustering'}
              </p>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white transition p-1">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Step indicator */}
          <div className="flex gap-2 mt-3">
            {[1, 2].map(s => (
              <div key={s} className={`h-1 flex-1 rounded-full transition-all ${s <= step ? 'bg-white' : 'bg-white/30'}`} />
            ))}
          </div>
        </div>

        <form data-testid="topics-create-form" onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {error && (
            <div data-testid="topics-name-error" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {advancedConfig && (
            <div className="mb-4 p-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-lg">🏆</span>
                <div className="flex-1">
                  <span className="font-semibold text-amber-800">Advanced Mode Active</span>
                  <span className="text-amber-700 text-sm ml-2">
                    {advancedConfig.algorithm?.toUpperCase()}
                    {advancedConfig.dim_reduction && ` + ${advancedConfig.dim_reduction.toUpperCase()}`}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* STEP 1: Name + View */}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Name</label>
                <input
                  data-testid="topics-name-input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition"
                  placeholder="e.g., Account Topics"
                  required
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description <span className="font-normal text-gray-400">(optional)</span></label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition"
                  placeholder="Describe what this topic configuration is for..."
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Source View</label>
                <select
                  data-testid="topics-datasource-select"
                  value={formData.source_view_id}
                  onChange={(e) => handleViewChange(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition"
                  required
                >
                  <option value="">Select a view...</option>
                  {views.map((view) => (
                    <option key={view.id} value={view.id}>{view.name}</option>
                  ))}
                </select>
                {loadingSchema && (
                  <p className="text-xs text-gray-400 mt-1.5">Loading columns...</p>
                )}
                {!loadingSchema && selectedView && viewSchema && meaningfulColumns.length > 0 && (
                  <p className="text-xs text-emerald-600 mt-1.5 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {meaningfulColumns.length} columns available for clustering
                  </p>
                )}
                {!loadingSchema && selectedView && viewSchema && meaningfulColumns.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1.5">This view has no text columns suitable for clustering.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Data Source <span className="text-red-500">*</span>
                  <span className="font-normal text-gray-400 ml-1.5">— used for incremental classification on sync</span>
                </label>
                <select
                  data-testid="topics-data-source-select"
                  value={formData.data_source_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, data_source_id: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition"
                  required
                >
                  <option value="">Select a data source…</option>
                  {dataSources.map(ds => (
                    <option key={ds.data_source_id || ds.id} value={ds.data_source_id || ds.id}>
                      {ds.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* STEP 2: Column mapping */}
          {step === 2 && (
            <div className="space-y-5">
              <div className="bg-violet-50 rounded-xl p-4 text-sm text-violet-800">
                <p className="font-semibold mb-1">How column roles work</p>
                <div className="space-y-1 text-xs text-violet-700">
                  <p><span className="font-semibold text-violet-800">Primary</span> — the main text to embed (e.g. Name, Subject, Description). Gets the most weight.</p>
                  <p><span className="font-semibold text-blue-700">Context</span> — supporting text that adds nuance but isn't the core signal.</p>
                  <p><span className="font-semibold text-emerald-700">Categorical</span> — discrete values (e.g. Industry, Stage) that act as hard cluster boundaries.</p>
                  <p className="text-violet-500 mt-1">Columns left as "Skip" are not included in the embedding.</p>
                </div>
              </div>

              {/* Column picker */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Columns from <span className="text-violet-600">{selectedView?.name}</span>
                </label>
                <div className="space-y-2">
                  {meaningfulColumns.map(col => {
                    const role = getRoleForColumn(col.name);
                    return (
                      <div key={col.name} className={`flex items-center gap-3 p-3 rounded-lg border transition ${role !== 'none' ? ROLE_COLORS[role] + ' border' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex-1">
                          <span className="font-medium text-sm">{col.name}</span>
                          <span className="text-xs ml-2 opacity-60">{col.type}</span>
                        </div>
                        <select
                          value={role}
                          onChange={(e) => updateColumnRole(col.name, e.target.value)}
                          className="text-sm border border-gray-300 rounded-lg px-2 py-1 bg-white focus:ring-2 focus:ring-violet-500"
                        >
                          <option value="none">Skip</option>
                          <option value="primary">Primary</option>
                          <option value="context">Context</option>
                          <option value="categorical">Categorical</option>
                        </select>
                      </div>
                    );
                  })}
                </div>
                {formData.column_mappings.length === 0 && (
                  <p className="text-xs text-red-500 mt-2">Select at least one column to continue.</p>
                )}
              </div>

              {/* Target + column name */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Target sObject</label>
                  <input
                    data-testid="topics-object-select"
                    type="text"
                    value={formData.target_sobject}
                    onChange={(e) => setFormData(prev => ({ ...prev, target_sobject: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition"
                    placeholder="e.g., Account"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Topic Column Name</label>
                  <input
                    type="text"
                    value={formData.topic_column_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, topic_column_name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition"
                    placeholder="e.g., account_topic"
                    required
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 -mt-3">
                Creates: <code className="bg-gray-100 px-1 rounded">bnstopic_{formData.topic_column_name || 'name'}_cluster</code>, <code className="bg-gray-100 px-1 rounded">_label</code>, <code className="bg-gray-100 px-1 rounded">_confidence</code>
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-200">
            {step === 2 && (
              <button type="button" onClick={() => setStep(1)} className="px-5 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition font-medium">
                &larr; Back
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition font-medium"
            >
              Cancel
            </button>
            {step === 1 ? (
              <button
                type="button"
                onMouseUp={advanceToStep2}
                disabled={!canProceedStep1}
                className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg hover:from-violet-700 hover:to-purple-700 transition font-semibold disabled:opacity-50 shadow-md"
              >
                Next: Map Columns &rarr;
              </button>
            ) : (
              <button
                data-testid="topics-submit-button"
                type="submit"
                disabled={isSubmitting || !canSubmit}
                className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg hover:from-violet-700 hover:to-purple-700 transition font-semibold disabled:opacity-50 shadow-md"
              >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Creating...
                </span>
              ) : (
                'Create Configuration'
              )}
            </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// TOPIC WORKBENCH - The Beautiful Clustering Interface
// ============================================================================

function TopicWorkbench({ config, columnValidation, onEditColumns, onClose }) {
  const dispatch = useDispatch();
  const [selectedSession, setSelectedSession] = useState(null);
  const [clusteringCountdown, setClusteringCountdown] = useState(null); // seconds remaining
  const [sessionState, setSessionState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Clustering parameters
  const [resolution, setResolution] = useState(1.0);
  const [pendingResolution, setPendingResolution] = useState(1.0);

  // Navigation
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [currentClusters, setCurrentClusters] = useState([]);

  // Level hierarchy navigation
  const [levels, setLevels] = useState([]);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [levelSessions, setLevelSessions] = useState({});
  // When drill-down is in progress for a level, store the child session ID here
  // so we can poll for completion and switch the view once clusters arrive.
  const [drillingSessionId, setDrillingSessionId] = useState(null);
  // Maps level number → the parent cluster label that was drilled into
  const [levelParentLabels, setLevelParentLabels] = useState({});
  // All levels' cluster data loaded in parallel: { [level]: { clusters, parent_cluster_id, session_id } }
  const [allLevelClusters, setAllLevelClusters] = useState({});
  // levelParentIds: { [level]: parentClusterId } — set at drill-down time so tree can attach children
  const [levelParentIds, setLevelParentIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`topics-tree-parents-${config?.id}`) || '{}'); } catch { return {}; }
  });
  // Selected node in the tree: { id, level, sessionId } | null
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  // Collapsed tree nodes: Set of "level:clusterId" strings — persisted per config
  const [collapsedNodes, setCollapsedNodes] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(`topics-tree-collapsed-${config?.id}`) || '[]')); } catch { return new Set(); }
  });

  // Persist tree state to localStorage whenever it changes
  useEffect(() => {
    if (!config?.id) return;
    try { localStorage.setItem(`topics-tree-collapsed-${config.id}`, JSON.stringify([...collapsedNodes])); } catch {}
  }, [collapsedNodes, config?.id]);
  useEffect(() => {
    if (!config?.id || Object.keys(levelParentIds).length === 0) return;
    try { localStorage.setItem(`topics-tree-parents-${config.id}`, JSON.stringify(levelParentIds)); } catch {}
  }, [levelParentIds, config?.id]);

  // Drag-to-merge state — draggingNode carries level+session context
  const [draggingClusterId, setDraggingClusterId] = useState(null);
  const [draggingNodeLevel, setDraggingNodeLevel] = useState(null);
  const [dragOverClusterId, setDragOverClusterId] = useState(null);

  // Sample records panel — keyed by "sessionId:clusterId"
  const [expandedCluster, setExpandedCluster] = useState(null);
  const [samples, setSamples] = useState({});
  const [loadingSamples, setLoadingSamples] = useState(null);

  // Relabeling
  const [editingLabel, setEditingLabel] = useState(null);
  const [newLabel, setNewLabel] = useState('');

  const loadLevels = useCallback(async () => {
    if (!config?.id) return;
    try {
      const res = await analyticsApi.listTopicLevels(config.id);
      const data = res.data || res;
      const lvls = data.levels || [];
      setLevels(lvls);
      const sessionMap = {};
      lvls.forEach(l => { sessionMap[l.level] = l.session_id; });
      setLevelSessions(sessionMap);
      // Load all levels' clusters in parallel for the tree view
      const fetches = lvls
        .filter(lv => lv.session_id)
        .map(lv =>
          analyticsApi.fetchTopicSession(lv.session_id)
            .then(r => { const d = r.data || r; return { level: lv.level, session_id: lv.session_id, clusters: d.clusters || [], parent_cluster_id: d.parent_cluster_id || null }; })
            .catch(() => ({ level: lv.level, session_id: lv.session_id, clusters: [], parent_cluster_id: null }))
        );
      const results = await Promise.all(fetches);
      const allClusters = {};
      results.forEach(r => { allClusters[r.level] = r; });
      setAllLevelClusters(allClusters);
      // Keep currentClusters in sync with level 0 for clustering/finalize handlers
      if (allClusters[0]?.clusters?.length > 0) setCurrentClusters(allClusters[0].clusters);
      return { lvls, sessionMap };
    } catch (err) {
      return { lvls: [], sessionMap: {} };
    }
  }, [config?.id]);

  // On open: find or create the active Level 0 session, then load it
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const { lvls, sessionMap } = await loadLevels();
        let sessionId = sessionMap[0]; // Level 0 session

        if (!sessionId) {
          // No level 0 session exists yet — try to create one
          try {
            const res = await analyticsApi.createTopicSession(config.id, { name: 'Level 0' });
            const created = res.data || res;
            sessionId = created.id;
          } catch (createErr) {
            if (createErr.response?.status === 400 &&
                createErr.response?.data?.message?.includes('Embeddings must be generated')) {
              // Embeddings not ready yet (config reset to draft after column change)
              // Show pending state — polling will auto-open workbench when embeddings complete
              setIsLoading(false);
              return;
            }
            throw createErr;
          }
        }

        setSelectedSession({ id: sessionId });
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load workbench');
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [config.id, loadLevels]);

  // Load session state when selected session changes
  useEffect(() => {
    if (!selectedSession?.id) return;
    const loadState = async () => {
      try {
        const res = await analyticsApi.fetchTopicSession(selectedSession.id);
        const data = res.data || res;
        setSessionState(data);
        if (data.clusters) {
          setCurrentClusters(data.clusters);
          setBreadcrumbs([{ label: 'All Clusters', clusters: data.clusters }]);
        }
      } catch (err) {
        console.error('Failed to load session state:', err);
      }
    };
    loadState();
  }, [selectedSession?.id]);

  // Poll the child session while drill-down is running asynchronously.
  useEffect(() => {
    if (!drillingSessionId) return;
    const interval = setInterval(async () => {
      try {
        const res = await analyticsApi.fetchTopicSession(drillingSessionId);
        const data = res.data || res;
        if (data.clusters && data.clusters.length > 0) {
          setCurrentClusters(data.clusters);
          setDrillingSessionId(null);
          await loadLevels();
        }
      } catch (_) { /* keep polling */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [drillingSessionId, loadLevels]);

  const handleRunClustering = async () => {
    if (!selectedSession) return;

    setIsProcessing(true);
    setError(null);

    // Start 20s countdown (matches backend timeout)
    const TIMEOUT_SEC = 20;
    setClusteringCountdown(TIMEOUT_SEC);
    const countdownRef = { interval: null };
    let remaining = TIMEOUT_SEC;
    countdownRef.interval = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearInterval(countdownRef.interval);
        setClusteringCountdown(null);
      } else {
        setClusteringCountdown(remaining);
      }
    }, 1000);

    try {
      // Estimate expected topics from resolution (roughly: resolution 1.0 ~= 10-15 topics)
      const expectedTopics = Math.max(5, Math.round(resolution * 15));

      // Build clustering request using config's saved algorithm settings
      const clusteringRequest = {
        method: config.clustering_algorithm || 'leiden',
        resolution: resolution,
        expected_topics: expectedTopics,  // For sample limiting on large datasets
      };

      // Add algorithm-specific params if using advanced clustering
      if (config.clustering_params && Object.keys(config.clustering_params).length > 0) {
        clusteringRequest.clustering_params = config.clustering_params;
      }

      // Add dimension reduction if configured
      if (config.dim_reduction_method) {
        clusteringRequest.dim_reduction_method = config.dim_reduction_method;
        if (config.dim_reduction_params && Object.keys(config.dim_reduction_params).length > 0) {
          clusteringRequest.dim_reduction_params = config.dim_reduction_params;
        }
      }

      const res = await analyticsApi.clusterTopicSession(selectedSession.id, clusteringRequest);
      clearInterval(countdownRef.interval);
      setClusteringCountdown(null);
      const data = res.data || res;
      setSessionState(data);
      if (data.clusters && data.clusters.length > 0) {
        setCurrentClusters(data.clusters);
        setBreadcrumbs([{ label: 'All Clusters', clusters: data.clusters }]);
      } else {
        // Returned async — show global progress panel and close workbench
        dispatch(setActiveTopicJob({
          type: 'clustering',
          status: 'running',
          configId: config.id,
          configName: config.name,
          sessionId: selectedSession.id,
          startedAt: new Date().toISOString(),
        }));
        onClose();
      }
    } catch (err) {
      clearInterval(countdownRef.interval);
      setClusteringCountdown(null);
      setError(err.response?.data?.message || err.message || 'Failed to run clustering');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDrill = async (cluster) => {
    if (!selectedSession) return;

    setIsProcessing(true);
    try {
      const res = await analyticsApi.drillTopicCluster(selectedSession.id, cluster.id, {
        resolution: resolution,
      });
      const data = res.data || res;
      if (data.clusters && data.clusters.length > 0) {
        setCurrentClusters(data.clusters);
        setBreadcrumbs(prev => [...prev, { label: cluster.label || `Cluster ${cluster.id}`, clusters: data.clusters, parentClusterId: cluster.id }]);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to drill into cluster');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBreadcrumbClick = (index) => {
    const crumb = breadcrumbs[index];
    setCurrentClusters(crumb.clusters);
    setBreadcrumbs(prev => prev.slice(0, index + 1));
  };

  const handleSwitchLevel = async (level) => {
    const sessionId = levelSessions[level];
    if (!sessionId) return;
    setCurrentLevel(level);
    try {
      const res = await analyticsApi.fetchTopicSession(sessionId);
      const data = res.data || res;
      if (data.clusters) {
        setCurrentClusters(data.clusters);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load level');
    }
  };

  const handleSplit = async (cluster) => {
    if (!selectedSession) return;
    setIsProcessing(true);
    try {
      const res = await analyticsApi.splitTopicCluster(selectedSession.id, cluster.id, { resolution });
      const data = res.data || res;
      if (data.clusters) {
        setCurrentClusters(data.clusters);
      }
      await loadLevels();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to split cluster');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDrillDown = async (cluster) => {
    if (!selectedSession) return;
    setIsProcessing(true);
    try {
      const res = await analyticsApi.drillDownTopicCluster(selectedSession.id, cluster.id, { resolution });
      const data = res.data || res;
      const newLevel = currentLevel + 1;
      if (data.child_session_id) {
        setLevelSessions(prev => ({ ...prev, [newLevel]: data.child_session_id }));
        setLevelParentLabels(prev => ({ ...prev, [newLevel]: cluster.label || `Cluster ${cluster.id}` }));
        setLevelParentIds(prev => ({ ...prev, [newLevel]: cluster.id }));
        // Switch to the new level immediately — clusters arrive async via polling below
        setCurrentLevel(newLevel);
        setCurrentClusters([]);
        if (!data.clusters || data.clusters.length === 0) {
          // Async drill-down — poll the child session until clusters appear
          setDrillingSessionId(data.child_session_id);
        }
      }
      if (data.clusters && data.clusters.length > 0) {
        setCurrentClusters(data.clusters);
        setDrillingSessionId(null);
      }
      await loadLevels();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to drill down');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMerge = async (sourceId, targetId) => {
    if (!selectedSession || !sourceId || !targetId || sourceId === targetId) return;

    const sourceLabel = currentClusters.find(c => c.id === sourceId)?.label || sourceId;
    const targetLabel = currentClusters.find(c => c.id === targetId)?.label || targetId;
    const suggestedLabel = targetLabel;

    const newLabelInput = window.prompt(
      `Merge "${sourceLabel}" into "${targetLabel}"\n\nLabel for merged cluster:`,
      suggestedLabel,
    );
    if (!newLabelInput) return;

    setIsProcessing(true);
    try {
      const res = await analyticsApi.mergeTopicClusters(selectedSession.id, [sourceId, targetId], newLabelInput);
      const data = res.data || res;
      if (data.clusters) {
        setCurrentClusters(data.clusters);
        // The hierarchy/detail panels render from allLevelClusters, not
        // currentClusters. Keep the selected level in sync after merging.
        const selectedLevel = selectedNodeId?.level ?? 0;
        setAllLevelClusters(prev => {
          const levelData = prev[selectedLevel];
          if (!levelData || levelData.session_id !== selectedSession.id) return prev;
          return { ...prev, [selectedLevel]: { ...levelData, clusters: data.clusters } };
        });
        setBreadcrumbs(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { ...updated[updated.length - 1], clusters: data.clusters };
          return updated;
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to merge clusters');
    } finally {
      setIsProcessing(false);
      setDraggingClusterId(null);
      setDragOverClusterId(null);
    }
  };

  const handleRelabel = async (clusterId) => {
    if (!selectedSession || !newLabel.trim()) return;

    setIsProcessing(true);
    try {
      const res = await analyticsApi.relabelTopicCluster(selectedSession.id, clusterId, newLabel.trim());
      const data = res.data || res;
      if (data.clusters) {
        setCurrentClusters(data.clusters);
        // Keep the selected tree node and detail panel in sync with the
        // server's updated labels.
        const selectedLevel = selectedNodeId?.level ?? 0;
        setAllLevelClusters(prev => {
          const levelData = prev[selectedLevel];
          if (!levelData || levelData.session_id !== selectedSession.id) return prev;
          return { ...prev, [selectedLevel]: { ...levelData, clusters: data.clusters } };
        });
        setBreadcrumbs(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { ...updated[updated.length - 1], clusters: data.clusters };
          return updated;
        });
      }
      setEditingLabel(null);
      setNewLabel('');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to relabel cluster');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLoadSamples = async (sessionId, clusterId) => {
    const cacheKey = `${sessionId}:${clusterId}`;
    if (samples[cacheKey]) {
      setExpandedCluster(expandedCluster === cacheKey ? null : cacheKey);
      return;
    }
    setLoadingSamples(cacheKey);
    try {
      const res = await analyticsApi.fetchTopicClusterSamples(sessionId, clusterId);
      const data = res.data || res;
      setSamples(prev => ({ ...prev, [cacheKey]: data.sample_texts || data.samples || data.sample_ids || [] }));
      setExpandedCluster(cacheKey);
    } catch (err) {
      console.error('Failed to load samples:', err);
    } finally {
      setLoadingSamples(null);
    }
  };

  const [finalizationStep, setFinalizationStep] = useState(null); // null, 'training', 'applying', 'done'
  const [classificationProgress, setClassificationProgress] = useState(null); // { progress_pct, classified_count, total_records }

  const handleFinalize = async () => {
    if (!selectedSession) return;

    const versionLabel = window.prompt(
      'Enter a version label for this model:\n\nThis will save your topic categories and apply them to all records.',
      `v${new Date().toISOString().slice(0, 10)}`
    );
    if (!versionLabel) return;

    setIsProcessing(true);
    setFinalizationStep('training');
    setClassificationProgress(null);
    setError(null);

    try {
      // Step 1: Finalize session → creates model
      const finalizeRes = await analyticsApi.finalizeTopicSession(selectedSession.id, versionLabel);
      const finalizeData = finalizeRes.data || finalizeRes;
      const modelId = finalizeData.model_id;

      // If we got a model ID, apply it to write to database
      if (modelId) {
        setFinalizationStep('applying');

        // Step 2: Start async classification
        const applyRes = await analyticsApi.applyTopicModel(modelId, { async: true });
        const applyData = applyRes.data || applyRes;

        // If async (status = in_progress), poll for completion
        if (applyData.status === 'in_progress') {
          // Poll for progress
          let completed = false;
          while (!completed) {
            await new Promise(resolve => setTimeout(resolve, 1500)); // Poll every 1.5s

            const progressRes = await analyticsApi.fetchClassificationProgress(modelId);
            const progress = progressRes.data || progressRes;

            setClassificationProgress({
              progress_pct: progress.progress_pct || 0,
              classified_count: progress.classified_count || 0,
              total_records: progress.total_records || 0,
              current_batch: progress.current_batch || 0,
              total_batches: progress.total_batches || 0,
            });

            if (progress.status === 'completed') {
              completed = true;
            } else if (progress.status === 'failed') {
              throw new Error(progress.error || 'Classification failed');
            }
          }
        }

        setFinalizationStep('done');
        setClassificationProgress(null);
        setTimeout(() => setFinalizationStep(null), 3000);

        // Show success
        if (import.meta.env.DEV) console.log('Applied topic model successfully');
      }

      // Refresh session to show finalized status
      const res = await analyticsApi.fetchTopicSession(selectedSession.id);
      setSessionState(res.data || res);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to finalize session');
      setFinalizationStep(null);
      setClassificationProgress(null);
    } finally {
      setIsProcessing(false);
    }
  };

  // Stats calculation
  const stats = useMemo(() => {
    if (!currentClusters || currentClusters.length === 0) return null;
    const totalRecords = currentClusters.reduce((sum, c) => sum + (c.count || 0), 0);
    const avgSize = totalRecords / currentClusters.length;
    const maxCluster = currentClusters.reduce((max, c) => (c.count || 0) > (max?.count || 0) ? c : max, null);
    return { totalRecords, avgSize, maxCluster, clusterCount: currentClusters.length };
  }, [currentClusters]);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-gray-50 rounded-2xl w-[95vw] h-[90vh] overflow-hidden flex flex-col shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="topic-workbench-modal-title">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 px-6 py-4 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <h2 id="topic-workbench-modal-title" className="text-xl font-bold text-white">{config.name}</h2>
                <p className="text-violet-100 text-sm">Topic Discovery Workbench</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white transition p-2 hover:bg-white/10 rounded-lg">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Missing Columns Banner */}
        {columnValidation && !columnValidation.valid && columnValidation.missing_columns?.length > 0 && (
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-amber-800 text-sm">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>
                <strong>Column mismatch:</strong> {columnValidation.missing_columns.join(', ')} {columnValidation.missing_columns.length === 1 ? 'is' : 'are'} not in your synced data. Future records won't be classified correctly.
              </span>
            </div>
            <button
              onClick={onEditColumns}
              className="ml-4 px-3 py-1 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700 transition flex-shrink-0"
            >
              Fix Columns
            </button>
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 border-b border-red-200 px-6 py-3 flex items-center justify-between">
            <span className="text-red-700 text-sm">{error}</span>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* No-columns gate — must configure columns before clustering */}
        {(!config.column_mappings || config.column_mappings.length === 0) && (
          <div className="flex-1 flex items-center justify-center bg-gray-50 p-10">
            <div className="max-w-lg text-center">
              <div className="text-6xl mb-6">🗂️</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No columns configured yet</h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Before clustering can run, you need to tell the AI which columns to analyze.
                Click the <strong>Edit Columns</strong> button — it's the small icon in the top-right
                of the config card that looks like this:
              </p>

              {/* Big visual of the actual button */}
              <div className="flex flex-col items-center gap-4 mb-8">
                <div className="bg-white rounded-2xl shadow-lg border-2 border-violet-200 p-6 flex items-center gap-4">
                  <div className="bg-violet-50 rounded-xl p-4 border-2 border-violet-400 shadow-md">
                    <svg className="w-8 h-8 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-gray-900">Edit Columns button</p>
                    <p className="text-sm text-gray-500">On the config card, top-right corner</p>
                  </div>
                </div>
                <svg className="w-6 h-6 text-gray-400 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                <button
                  onClick={onEditColumns}
                  className="bg-gradient-to-r from-violet-600 to-purple-600 text-white px-8 py-4 rounded-2xl font-bold text-lg shadow-xl hover:from-violet-700 hover:to-purple-700 transition transform hover:-translate-y-0.5 flex items-center gap-3"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  Configure Columns Now
                </button>
              </div>

              <p className="text-xs text-gray-400">
                After saving your column choices, re-open this workbench to start clustering.
              </p>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className={`flex-1 flex overflow-hidden ${(!config.column_mappings || config.column_mappings.length === 0) ? 'hidden' : ''}`}>
          {/* Main Panel */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {isLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-violet-600 border-t-transparent" />
              </div>
            ) : !selectedSession && !error ? (
              <div className="flex-1 flex items-center justify-center bg-gray-50 p-10">
                <div className="text-center max-w-sm">
                  <div className="relative mx-auto mb-6 w-16 h-16">
                    <div className="w-16 h-16 border-4 border-violet-200 rounded-full" />
                    <div className="absolute inset-0 border-4 border-transparent border-t-violet-600 rounded-full animate-spin" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Generating Embeddings</h3>
                  <p className="text-gray-500 text-sm">
                    Your column selection is being processed. The workbench will open automatically when ready.
                  </p>
                </div>
              </div>
            ) : selectedSession ? (
              <>
                {/* Controls Bar */}
                <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
                  {/* Tree legend — shows levels present */}
                  <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-100">
                    {levels.map((lv) => {
                      const label = lv.name || (lv.level === 0 ? 'Root topics' : `Level ${lv.level}`);
                      const dotColor = ['bg-violet-500','bg-blue-500','bg-emerald-500','bg-amber-500','bg-rose-500'][lv.level % 5];
                      return (
                        <span key={lv.level} className="flex items-center gap-1.5 text-xs text-gray-500">
                          <span className={`w-2 h-2 rounded-full ${dotColor}`} />
                          {label}
                          {lv.cluster_count ? <span className="font-semibold text-gray-700">{lv.cluster_count}</span> : null}
                        </span>
                      );
                    })}
                    {levels.length <= 1 && currentClusters.length > 0 && (
                      <span className="text-xs text-gray-400 italic">— select a cluster and click "Go deeper" to add sub-levels</span>
                    )}
                    {levels.length > 1 && (
                      <span className="text-xs text-gray-400 ml-auto italic">Sub-levels shown indented in tree</span>
                    )}
                  </div>
                  <div className="flex items-center gap-6">
                  {/* Algorithm Badge - show when using non-default */}
                  {config.clustering_algorithm && config.clustering_algorithm !== 'leiden' && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
                      <span className="text-sm">🏆</span>
                      <span className="text-sm font-medium text-amber-800">
                        {config.clustering_algorithm.toUpperCase()}
                        {config.dim_reduction_method && ` + ${config.dim_reduction_method.toUpperCase()}`}
                      </span>
                    </div>
                  )}
                  {/* Resolution Slider */}
                  <div className="flex items-center gap-4 flex-1">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Resolution</label>
                    <div className="flex-1 max-w-xs">
                      <input
                        type="range"
                        min="0.1"
                        max="3"
                        step="0.1"
                        value={pendingResolution}
                        onChange={(e) => setPendingResolution(parseFloat(e.target.value))}
                        onMouseUp={() => setResolution(pendingResolution)}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-violet-600"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>Fewer</span>
                        <span className="font-semibold text-violet-600">{pendingResolution.toFixed(1)}</span>
                        <span>More</span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleRunClustering}
                      disabled={isProcessing}
                      className="px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white text-sm font-semibold rounded-lg hover:from-violet-600 hover:to-purple-600 transition shadow-md disabled:opacity-50 flex items-center gap-2"
                    >
                      {isProcessing ? (
                        <>
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          {clusteringCountdown != null
                            ? `Clustering… ${clusteringCountdown}s`
                            : 'Clustering…'}
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {currentClusters.length > 0 ? 'Re-cluster' : 'Run Clustering'}
                        </>
                      )}
                    </button>

                    {currentClusters.length > 0 && (
                      <>
                        {sessionState?.status !== 'finalized' && (
                          <button
                            onClick={handleFinalize}
                            disabled={isProcessing}
                            className="px-4 py-2 bg-emerald-500 text-white text-sm font-semibold rounded-lg hover:bg-emerald-600 transition shadow-md disabled:opacity-50 flex items-center gap-2"
                          >
                            {finalizationStep === 'training' ? (
                              <>
                                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                Training model...
                              </>
                            ) : finalizationStep === 'applying' ? (
                              <>
                                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                {classificationProgress
                                  ? `Classifying... ${Math.round(classificationProgress.progress_pct)}%`
                                  : 'Applying to records...'}
                              </>
                            ) : finalizationStep === 'done' ? (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Done!
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Save & Apply
                              </>
                            )}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  </div>
                </div>

                {/* Drag-to-merge hint */}
                {draggingClusterId && (
                  <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 text-amber-800 text-sm font-medium text-center">
                    Drop onto another cluster at the same level to merge
                  </div>
                )}

                {/* Stats Bar */}
                {stats && (
                  <div className="bg-gradient-to-r from-violet-50 to-purple-50 border-b border-violet-100 px-6 py-3 flex items-center gap-8 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-violet-100 rounded-lg">
                        <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Clusters</div>
                        <div className="text-sm font-bold text-gray-900">{stats.clusterCount}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-violet-100 rounded-lg">
                        <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Total Records</div>
                        <div className="text-sm font-bold text-gray-900">{stats.totalRecords.toLocaleString()}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-violet-100 rounded-lg">
                        <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Avg Size</div>
                        <div className="text-sm font-bold text-gray-900">{Math.round(stats.avgSize).toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tree + Detail Split */}
                <div className="flex-1 flex overflow-hidden">
                  {/* Left: Hierarchy Tree */}
                  <div className="w-[38%] border-r border-gray-200 overflow-y-auto flex flex-col">
                    {Object.keys(allLevelClusters).length === 0 && drillingSessionId ? (
                      <div className="flex flex-col items-center justify-center flex-1 p-8 text-center">
                        <svg className="w-10 h-10 text-violet-400 animate-spin mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        <p className="text-sm text-gray-500">Creating sub-level…</p>
                      </div>
                    ) : Object.keys(allLevelClusters).length === 0 ? (
                      <div className="flex flex-col items-center justify-center flex-1 p-8 text-center">
                        <svg className="w-10 h-10 text-violet-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                        </svg>
                        <p className="text-sm font-medium text-gray-700 mb-1">No topics yet</p>
                        <p className="text-xs text-gray-400">Run Clustering to discover topics</p>
                      </div>
                    ) : (
                      <ClusterTreePanel
                        allLevelClusters={allLevelClusters}
                        levels={levels}
                        levelParentIds={levelParentIds}
                        selectedNodeId={selectedNodeId}
                        collapsedNodes={collapsedNodes}
                        draggingClusterId={draggingClusterId}
                        draggingNodeLevel={draggingNodeLevel}
                        dragOverClusterId={dragOverClusterId}
                        drillingSessionId={drillingSessionId}
                        onSelectNode={(id, level, sessionId) => {
                          setSelectedNodeId({ id, level, sessionId });
                          setSelectedSession({ id: sessionId });
                          setCurrentLevel(level);
                          const levelData = allLevelClusters[level];
                          if (levelData?.clusters?.length > 0) setCurrentClusters(levelData.clusters);
                        }}
                        onToggleCollapse={(id, level) => {
                          const key = `${level}:${id}`;
                          setCollapsedNodes(prev => {
                            const next = new Set(prev);
                            next.has(key) ? next.delete(key) : next.add(key);
                            return next;
                          });
                        }}
                        onDragStart={(clusterId, level) => { setDraggingClusterId(clusterId); setDraggingNodeLevel(level); }}
                        onDragEnd={() => { setDraggingClusterId(null); setDraggingNodeLevel(null); setDragOverClusterId(null); }}
                        onDragOver={(e, clusterId) => { e.preventDefault(); setDragOverClusterId(clusterId); }}
                        onDragLeave={() => setDragOverClusterId(null)}
                        onDrop={(targetId, targetLevel, targetSessionId) => {
                          if (draggingClusterId && draggingClusterId !== targetId && draggingNodeLevel === targetLevel) {
                            setSelectedSession({ id: targetSessionId });
                            const lvData = allLevelClusters[targetLevel];
                            if (lvData?.clusters) setCurrentClusters(lvData.clusters);
                            handleMerge(draggingClusterId, targetId);
                          }
                          setDraggingClusterId(null);
                          setDraggingNodeLevel(null);
                          setDragOverClusterId(null);
                        }}
                      />
                    )}
                  </div>

                  {/* Right: Detail Panel */}
                  <div className="flex-1 overflow-y-auto">
                    {selectedNodeId ? (() => {
                      const lvData = allLevelClusters[selectedNodeId.level];
                      const cluster = lvData?.clusters?.find(c => c.id === selectedNodeId.id);
                      if (!cluster) return null;
                      const lvlTotalRecords = (lvData?.clusters || []).reduce((s, c) => s + (c.count || 0), 0) || 1;
                      const cacheKey = `${selectedNodeId.sessionId}:${cluster.id}`;
                      return (
                        <ClusterDetailPanel
                          cluster={cluster}
                          level={selectedNodeId.level}
                          sessionId={selectedNodeId.sessionId}
                          totalRecords={lvlTotalRecords}
                          samples={samples[cacheKey]}
                          loadingSamples={loadingSamples === cacheKey}
                          isEditing={editingLabel === cluster.id}
                          newLabel={editingLabel === cluster.id ? newLabel : ''}
                          isProcessing={isProcessing}
                          drillingSessionId={drillingSessionId}
                          onLoadSamples={() => handleLoadSamples(selectedNodeId.sessionId, cluster.id)}
                          onSplit={() => { setSelectedSession({ id: selectedNodeId.sessionId }); setCurrentClusters(lvData?.clusters || []); handleSplit(cluster); }}
                          onDrillDown={() => { setSelectedSession({ id: selectedNodeId.sessionId }); setCurrentClusters(lvData?.clusters || []); handleDrillDown(cluster); }}
                          onStartEdit={() => { setEditingLabel(cluster.id); setNewLabel(cluster.label || ''); }}
                          onCancelEdit={() => { setEditingLabel(null); setNewLabel(''); }}
                          onSaveLabel={() => { setSelectedSession({ id: selectedNodeId.sessionId }); handleRelabel(cluster.id); }}
                          onLabelChange={setNewLabel}
                        />
                      );
                    })() : (
                      <div className="flex flex-col items-center justify-center h-full p-8 text-center text-gray-400">
                        <svg className="w-12 h-12 mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
                        </svg>
                        <p className="text-sm">Select a cluster to see details</p>
                      </div>
                    )}
                  </div>
                </div>

              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CLUSTER TREE PANEL + TREE NODE + DETAIL PANEL
// ============================================================================

const LEVEL_COLORS = ['bg-violet-500', 'bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500'];
const LEVEL_LIGHT  = ['bg-violet-50 border-violet-200', 'bg-blue-50 border-blue-200', 'bg-emerald-50 border-emerald-200', 'bg-amber-50 border-amber-200', 'bg-rose-50 border-rose-200'];

function buildClusterTree(allLevelClusters, levelNums, levelParentIds) {
  const sorted = [...levelNums].sort((a, b) => a - b);
  const roots = (allLevelClusters[0]?.clusters || []).map(c => ({
    ...c, level: 0, sessionId: allLevelClusters[0]?.session_id, children: [],
  }));
  for (let i = 1; i < sorted.length; i++) {
    const lv = sorted[i];
    const lvData = allLevelClusters[lv];
    if (!lvData) continue;
    // Use stored parent ID from drill-down time (reliable), fall back to session field
    const parentId = (levelParentIds && levelParentIds[lv]) || lvData.parent_cluster_id;
    if (!parentId) continue;
    const children = (lvData.clusters || []).map(c => ({ ...c, level: lv, sessionId: lvData.session_id, children: [] }));
    const attach = (nodes) => {
      for (const n of nodes) {
        if (n.id === parentId && n.level === lv - 1) { n.children = children; return true; }
        if (n.children.length && attach(n.children)) return true;
      }
      return false;
    };
    attach(roots);
  }
  return roots;
}

function ClusterTreePanel({ allLevelClusters, levels, levelParentIds, selectedNodeId, collapsedNodes, draggingClusterId, draggingNodeLevel, dragOverClusterId, drillingSessionId, onSelectNode, onToggleCollapse, onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop }) {
  const levelNums = levels.map(l => l.level);
  const roots = buildClusterTree(allLevelClusters, levelNums, levelParentIds);

  const renderNode = (node) => {
    const isCollapsed = collapsedNodes.has(`${node.level}:${node.id}`);
    const hasChildren = node.children.length > 0 || (drillingSessionId && allLevelClusters[node.level + 1] === undefined);
    const isDrilling = drillingSessionId && node.id === selectedNodeId?.id && node.level === selectedNodeId?.level - 1;
    return (
      <div key={`${node.level}:${node.id}`}>
        <ClusterTreeNode
          node={node}
          isSelected={selectedNodeId?.id === node.id && selectedNodeId?.level === node.level}
          isCollapsed={isCollapsed}
          hasChildren={hasChildren}
          isDrilling={isDrilling}
          isDragOver={dragOverClusterId === node.id}
          canDrop={draggingClusterId && draggingClusterId !== node.id && draggingNodeLevel === node.level}
          isDimmed={draggingClusterId && draggingNodeLevel !== null && draggingNodeLevel !== node.level}
          onSelect={() => onSelectNode(node.id, node.level, node.sessionId)}
          onToggle={() => onToggleCollapse(node.id, node.level)}
          onDragStart={() => onDragStart(node.id, node.level)}
          onDragEnd={onDragEnd}
          onDragOver={(e) => onDragOver(e, node.id)}
          onDragLeave={onDragLeave}
          onDrop={() => onDrop(node.id, node.level, node.sessionId)}
        />
        {!isCollapsed && node.children.length > 0 && (
          <div>{node.children.map(renderNode)}</div>
        )}
        {!isCollapsed && isDrilling && (
          <div style={{ paddingLeft: `${12 + (node.level + 1) * 20}px` }} className="py-2 flex items-center gap-2 text-xs text-violet-500">
            <svg className="w-3.5 h-3.5 animate-spin flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Creating sub-level…
          </div>
        )}
      </div>
    );
  };

  return <div className="py-1">{roots.map(renderNode)}</div>;
}

function ClusterTreeNode({ node, isSelected, isCollapsed, hasChildren, isDrilling, isDragOver, canDrop, isDimmed, onSelect, onToggle, onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop }) {
  const dotColor = LEVEL_COLORS[node.level % LEVEL_COLORS.length];
  return (
    <div
      draggable
      style={{ paddingLeft: `${12 + node.level * 20}px` }}
      className={`flex items-center gap-2 pr-3 py-2 cursor-pointer select-none transition-colors
        ${isSelected ? 'bg-violet-50 border-l-2 border-violet-500' : 'border-l-2 border-transparent hover:bg-gray-50'}
        ${isDragOver && canDrop ? 'bg-amber-50 border-l-2 !border-amber-400' : ''}
        ${isDimmed ? 'opacity-40' : ''}
      `}
      onClick={onSelect}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Collapse toggle */}
      <button
        onClick={e => { e.stopPropagation(); if (hasChildren) onToggle(); }}
        className={`w-4 h-4 flex-shrink-0 flex items-center justify-center text-gray-400 hover:text-gray-600 ${!hasChildren ? 'invisible' : ''}`}
      >
        <svg className={`w-3 h-3 transition-transform ${isCollapsed ? '' : 'rotate-90'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      {/* Color dot */}
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor}`} />
      {/* Label */}
      <span className="flex-1 text-sm font-medium text-gray-800 truncate">{node.label || `Cluster ${node.id}`}</span>
      {/* Count */}
      <span className="text-xs text-gray-400 whitespace-nowrap">{(node.count || 0).toLocaleString()} records</span>
      {/* % pill */}
      {node.percentage !== undefined && (
        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${LEVEL_LIGHT[node.level % LEVEL_LIGHT.length]} border`}>
          {parseFloat(node.percentage).toFixed(1)}%
        </span>
      )}
      {/* Drag handle */}
      <span className="text-gray-300 cursor-grab ml-0.5 text-base leading-none select-none">⠿</span>
    </div>
  );
}

function ClusterDetailPanel({ cluster, level, sessionId, totalRecords, samples, loadingSamples, isEditing, newLabel, isProcessing, drillingSessionId, onLoadSamples, onSplit, onDrillDown, onStartEdit, onCancelEdit, onSaveLabel, onLabelChange }) {
  const dotColor = LEVEL_COLORS[level % LEVEL_COLORS.length];
  const pct = ((cluster.count || 0) / totalRecords * 100).toFixed(1);
  const [refineOpen, setRefineOpen] = useState(false);
  const refineRef = useRef(null);
  useEffect(() => {
    if (!refineOpen) return;
    const handler = (e) => { if (refineRef.current && !refineRef.current.contains(e.target)) setRefineOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [refineOpen]);

  return (
    <div className="p-6 h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className={`w-3 h-3 rounded-full flex-shrink-0 ${dotColor}`} />
          {isEditing ? (
            <div className="flex items-center gap-2 flex-1">
              <input
                autoFocus
                value={newLabel}
                onChange={e => onLabelChange(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') onSaveLabel(); if (e.key === 'Escape') onCancelEdit(); }}
                className="flex-1 px-3 py-1.5 text-sm border border-violet-300 rounded-lg focus:ring-2 focus:ring-violet-500"
              />
              <button onClick={onSaveLabel} className="px-3 py-1.5 bg-violet-600 text-white text-sm rounded-lg hover:bg-violet-700">Save</button>
              <button onClick={onCancelEdit} className="px-3 py-1.5 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200">Cancel</button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <h3 className="text-lg font-bold text-gray-900 truncate">{cluster.label || `Cluster ${cluster.id}`}</h3>
              <button onClick={onStartEdit} className="text-gray-400 hover:text-violet-600 flex-shrink-0">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-4 mb-5">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">{(cluster.count || 0).toLocaleString()}</div>
          <div className="text-xs text-gray-500">records</div>
        </div>
        <div className={`px-3 py-1.5 rounded-full text-sm font-semibold ${LEVEL_LIGHT[level % LEVEL_LIGHT.length]} border`}>{pct}%</div>
        <div className="text-xs text-gray-400">Level {level}</div>
      </div>

      {/* Sample quotes */}
      {cluster.sample_texts && cluster.sample_texts.length > 0 && (
        <div className="mb-5">
          <p className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-2">Sample Records</p>
          <div className="space-y-1.5">
            {cluster.sample_texts.slice(0, 3).map((t, i) => (
              <p key={i} className="text-sm text-gray-600 italic bg-gray-50 rounded-lg px-3 py-2 line-clamp-2">"{t}"</p>
            ))}
          </div>
        </div>
      )}

      {/* Load more samples */}
      <div className="mb-5">
        <button
          onClick={onLoadSamples}
          disabled={loadingSamples}
          className="text-sm text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1.5"
        >
          {loadingSamples ? (
            <><svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Loading…</>
          ) : samples ? `${samples.length} samples loaded` : 'Load samples'}
        </button>
        {samples && samples.length > 0 && (
          <div className="mt-2 space-y-1.5 max-h-40 overflow-y-auto">
            {samples.map((t, i) => <p key={i} className="text-xs text-gray-600 bg-gray-50 rounded px-2 py-1 italic">"{t}"</p>)}
          </div>
        )}
      </div>

      {/* Refine actions */}
      <div className="border-t border-gray-100 pt-4">
        <p className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-3">Refine</p>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={onSplit}
            disabled={isProcessing || !!drillingSessionId}
            className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-sm font-medium hover:bg-amber-100 transition disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
            Split into sub-topics
          </button>
          <button
            onClick={onDrillDown}
            disabled={isProcessing || !!drillingSessionId}
            className="flex items-center gap-2 px-4 py-2.5 bg-violet-50 text-violet-700 border border-violet-200 rounded-lg text-sm font-medium hover:bg-violet-100 transition disabled:opacity-50"
          >
            {drillingSessionId ? (
              <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Creating sub-level…</>
            ) : (
              <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>Go deeper ↓</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CLUSTER CARD - Individual Cluster Display
// ============================================================================

function ClusterCard({
  cluster,
  index,
  isDragOver,
  isExpanded,
  isEditing,
  samples,
  loadingSamples,
  newLabel,
  totalRecords,
  onSplit,
  onDrillDown,
  currentLevel,
  hasChildren,
  onLoadSamples,
  onStartEdit,
  onCancelEdit,
  onSaveLabel,
  onLabelChange,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
}) {
  const [refineOpen, setRefineOpen] = useState(false);
  const refineRef = useRef(null);

  useEffect(() => {
    if (!refineOpen) return;
    const handler = (e) => {
      if (refineRef.current && !refineRef.current.contains(e.target)) setRefineOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [refineOpen]);
  const percentage = ((cluster.count || 0) / totalRecords * 100).toFixed(1);

  // Color palette for clusters
  const colors = [
    'from-violet-500 to-purple-500',
    'from-blue-500 to-indigo-500',
    'from-emerald-500 to-teal-500',
    'from-amber-500 to-orange-500',
    'from-rose-500 to-pink-500',
    'from-cyan-500 to-sky-500',
    'from-lime-500 to-green-500',
    'from-fuchsia-500 to-purple-500',
  ];
  const colorClass = colors[index % colors.length];

  return (
    <div
      data-testid={`topics-cluster-${cluster.id}`}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`bg-white rounded-xl shadow-md border-2 transition-all duration-200 cursor-grab active:cursor-grabbing ${
        isDragOver
          ? 'border-amber-400 shadow-amber-100 scale-[1.02]'
          : 'border-transparent hover:shadow-lg hover:border-violet-200'
      }`}
    >
      {/* Card Header with Color Bar */}
      <div className={`h-2 bg-gradient-to-r rounded-t-xl ${colorClass}`} />

      <div className="p-4">
        {/* Label Section */}
        <div className="flex items-start justify-between mb-3">
          {isEditing ? (
            <div className="flex-1 flex items-center gap-2">
              <input
                type="text"
                value={newLabel}
                onChange={(e) => onLabelChange(e.target.value)}
                className="flex-1 px-3 py-1.5 text-sm border border-violet-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                placeholder="Enter new label..."
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSaveLabel();
                  if (e.key === 'Escape') onCancelEdit();
                }}
              />
              <button
                onClick={onSaveLabel}
                className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
              <button
                onClick={onCancelEdit}
                className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <>
              <div className="flex-1">
                <h4 data-testid={`topics-cluster-label-${cluster.id}`} className="font-semibold text-gray-900 leading-tight">
                  {cluster.label || `Cluster ${cluster.id}`}
                </h4>
                {cluster.auto_label && !cluster.user_label && (
                  <span className="text-xs text-violet-500 flex items-center gap-1 mt-0.5">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    AI generated
                  </span>
                )}
                {cluster.parent_cluster_id && (
                  <div style={{ fontSize: '10px', color: '#8b5cf6', background: '#f5f3ff', borderRadius: '4px', padding: '1px 6px', display: 'inline-block', marginTop: '4px' }}>
                    ↳ sub-group of {cluster.parent_cluster_id}
                  </div>
                )}
              </div>
              <button
                data-testid={`topics-cluster-edit-${cluster.id}`}
                onClick={onStartEdit}
                className="p-1.5 text-gray-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition"
                title="Edit label"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-1.5">
            <span className="text-2xl font-bold text-gray-900">{(cluster.count || 0).toLocaleString()}</span>
            <span className="text-sm text-gray-500">records</span>
          </div>
          <div className={`px-2 py-0.5 rounded-full text-xs font-semibold bg-gradient-to-r ${colorClass} text-white`}>
            {percentage}%
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-3">
          <div
            className={`h-full bg-gradient-to-r ${colorClass} rounded-full transition-all duration-500`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>

        {/* Sample Preview */}
        {cluster.sample_texts && cluster.sample_texts.length > 0 && (
          <div className="text-xs text-gray-500 mb-3 line-clamp-2 italic">
            "{cluster.sample_texts[0]?.substring(0, 100)}..."
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={onLoadSamples}
            className="flex-1 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition flex items-center justify-center gap-1.5"
          >
            {loadingSamples ? (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            )}
            Samples
          </button>

          {/* Refine dropdown — replaces the old Split + Drill buttons */}
          <div className="flex-1 relative" ref={refineRef}>
            <button
              onClick={() => setRefineOpen(o => !o)}
              className="w-full py-2 text-sm font-medium bg-violet-100 text-violet-700 rounded-lg hover:bg-violet-200 transition flex items-center justify-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              Refine
              <svg className={`w-3 h-3 transition-transform ${refineOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {refineOpen && (
              <div className="absolute bottom-full mb-1.5 left-0 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-20 w-80">
                <button
                  data-testid={`topics-split-${cluster.id}`}
                  onClick={() => { onSplit(); setRefineOpen(false); }}
                  className="w-full text-left px-4 py-3 hover:bg-amber-50 transition group"
                >
                  <div className="flex items-center gap-2 mb-0.5">
                    <svg className="w-4 h-4 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    <span className="text-sm font-semibold text-gray-900">Split into sub-topics</span>
                  </div>
                  <p className="text-xs text-gray-500 pl-6">Break this cluster into finer groups at this level. This cluster is replaced by its children.</p>
                </button>

                <div className="border-t border-gray-100" />

                <button
                  data-testid={`topics-drill-down-${cluster.id}`}
                  onClick={() => { onDrillDown(); setRefineOpen(false); }}
                  className="w-full text-left px-4 py-3 hover:bg-violet-50 transition group"
                >
                  <div className="flex items-center gap-2 mb-0.5">
                    <svg className="w-4 h-4 text-violet-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                    <span className="text-sm font-semibold text-gray-900">
                      {hasChildren ? 'View sub-level ↓' : 'Go deeper ↓'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 pl-6">
                    {hasChildren
                      ? 'This cluster already has a sub-level. Switch to it to explore.'
                      : 'Create a new level below showing just the sub-groups of this cluster. This cluster stays here.'}
                  </p>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Expanded Samples */}
        {isExpanded && samples && samples.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Sample Records</h5>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {samples.slice(0, 5).map((sample, i) => (
                <div key={i} className="p-2 bg-gray-50 rounded-lg text-xs text-gray-700">
                  {typeof sample === 'string' ? (
                    <div className="line-clamp-2">{sample}</div>
                  ) : (
                    <>
                      <div className="font-medium text-gray-900 mb-1">{sample.id || sample.Id || `Record ${i + 1}`}</div>
                      {sample.text && <div className="line-clamp-2">{sample.text}</div>}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TopicsTab;
